<template>
  <!-- ══ Strip — compartilha o trilho de 60px com o AgentChatDrawer; só um dos
       dois fica visível por vez (useChatRailMode) ══ -->
  <div v-if="railMode === 'client' && !hiddenOnFoco" class="cdr-strip" :class="{ 'is-expanded': panelVisible }">

    <!-- Cap — alinha com cabeçalho do sidebar -->
    <div class="cdr-strip-cap"></div>

    <!-- Cabeçalho do strip -->
    <div class="cdr-strip-head" @click="toggleList" :title="listOpen ? 'Fechar' : 'Minhas conversas'">
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
      <span v-if="myConvs.length" class="cdr-strip-badge">{{ myConvs.length > 9 ? '9+' : myConvs.length }}</span>
    </div>

    <!-- Lista de avatares -->
    <div class="cdr-av-list-wrap">
      <button v-if="canScrollUp" class="cdr-scroll-btn up" @click="scrollStrip(-1)" title="Rolar para cima">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="18 15 12 9 6 15"/>
        </svg>
      </button>

      <div class="cdr-av-list" ref="stripListEl" @scroll="updateStripScrollState">
        <div
          v-for="conv in myConvs"
          :key="conv.id"
          class="cdr-av-item"
          :class="{ active: selConvId === conv.id }"
          @click="selectConv(conv)"
          @mouseenter="showTT(conv, $event)"
          @mouseleave="hideTT"
        >
          <div class="cdr-av" :style="{ background: avatarColor(conv.contact.name) }">
            <img
              v-if="conv.contact.thumbnail"
              :src="conv.contact.thumbnail"
              :alt="conv.contact.name"
              @error="(e) => (e.target as HTMLImageElement).style.display='none'"
            />
            <span v-else>{{ initials(conv.contact.name) }}</span>
          </div>
          <span class="cdr-status-dot" :class="statusDot(conv)"></span>
          <span v-if="conv.unread_count > 0 && !localReadConvIds.has(conv.id)" class="cdr-av-unread-badge">{{ conv.unread_count > 9 ? '9+' : conv.unread_count }}</span>
          <span v-else-if="conv.status === 'snoozed'" class="cdr-av-snooze-badge">
            <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
          </span>
        </div>

        <div v-if="!myConvs.length" class="cdr-strip-empty">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        </div>
      </div>

      <button v-if="canScrollDown" class="cdr-scroll-btn down" @click="scrollStrip(1)" title="Rolar para baixo">
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
    </div>

    <!-- Rodapé — alterna para o chat entre agentes (ícone = chat de clientes, o modo atual) -->
    <div v-if="can('agentChat.view')" class="cdr-strip-footer" @click="toggleRailMode" title="Alternar para chat entre agentes">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
      </svg>
      <span v-if="agentChatUnread > 0" class="cdr-strip-footer-badge">{{ agentChatUnread > 9 ? '9+' : agentChatUnread }}</span>
    </div>

    <!-- Card com dados da conversa ao passar o mouse — retângulo com bordas
         arredondadas (Teleport pra renderizar acima de tudo) -->
    <Teleport to="body">
      <transition name="tt-pill">
        <div
          v-if="tt"
          class="cdr-tt-card"
          :style="{ top: tt.y + 'px', right: (STRIP_W + 8) + 'px' }"
        >
          <div class="cdr-ttc-head">
            <div class="cdr-ttc-av" :style="{ background: avatarColor(tt.conv.contact.name) }">
              <img
                v-if="tt.conv.contact.thumbnail"
                :src="tt.conv.contact.thumbnail"
                :alt="tt.conv.contact.name"
                @error="(e) => (e.target as HTMLImageElement).style.display='none'"
              />
              <span v-else>{{ initials(tt.conv.contact.name) }}</span>
              <span class="cdr-ttc-av-dot" :class="statusDot(tt.conv)"></span>
            </div>
            <div class="cdr-ttc-headinfo">
              <div class="cdr-ttc-row1">
                <span class="cdr-ttc-name">{{ tt.conv.contact.name || tt.conv.contact.phone || '#' + tt.conv.id }}</span>
                <span v-if="tt.conv.unread_count > 0 && !localReadConvIds.has(tt.conv.id)" class="cdr-ttc-unread">
                  {{ tt.conv.unread_count > 9 ? '9+' : tt.conv.unread_count }}
                </span>
              </div>
              <span class="cdr-ttc-id">#{{ tt.conv.id }}</span>
            </div>
          </div>

          <div class="cdr-ttc-divider"></div>

          <div class="cdr-ttc-body">
            <span v-if="tt.conv.contact.phone" class="cdr-ttc-row cdr-ttc-row--phone">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.44 2 2 0 0 1 3.59 2.24h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              {{ fmtPhone(tt.conv.contact.phone) }}
            </span>
            <span v-if="tt.conv.team" class="cdr-ttc-row">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              {{ tt.conv.team.name }}
            </span>
            <span v-if="tt.conv.assignee" class="cdr-ttc-row">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              {{ tt.conv.assignee.name }}
            </span>
            <span class="cdr-ttc-status">
              <span class="cdr-ttc-dot" :class="statusDot(tt.conv)"></span>
              {{ statusLabel(tt.conv) }}
            </span>
          </div>
        </div>
      </transition>
    </Teleport>

    <!-- Tooltip completo (legado) -->
    <Teleport to="body">
      <transition name="tt-fade">
        <div
          v-if="false"
          class="cdr-tt"
          :style="{ top: tt?.y + 'px', right: (STRIP_W + 10) + 'px' }"
        >
          <!-- Cabeçalho: avatar + nome + empresa -->
          <div class="cdr-tt-head">
            <div class="cdr-tt-av" :style="{ background: avatarColor(tt.conv.contact.name) }">
              <img
                v-if="contactDetail?.thumbnail"
                :src="contactDetail.thumbnail"
                :alt="tt.conv.contact.name"
                @error="(e) => (e.target as HTMLImageElement).style.display='none'"
              />
              <span v-else>{{ initials(tt.conv.contact.name) }}</span>
            </div>
            <div class="cdr-tt-head-info">
              <div class="cdr-tt-name">{{ tt.conv.contact.name }}</div>
              <div v-if="contactDetail?.additional_attributes?.company_name" class="cdr-tt-company">
                {{ contactDetail.additional_attributes.company_name }}
              </div>
              <div v-else-if="contactLoading === tt.conv.contact.id" class="cdr-tt-company" style="opacity:.4">
                Carregando…
              </div>
            </div>
          </div>

          <div class="cdr-tt-divider"></div>

          <!-- Contato -->
          <div v-if="tt.conv.contact.phone" class="cdr-tt-row">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.44 2 2 0 0 1 3.59 2.24h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            {{ tt.conv.contact.phone }}
          </div>
          <div v-if="contactDetail?.email" class="cdr-tt-row">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            {{ contactDetail.email }}
          </div>
          <div v-if="contactDetail?.additional_attributes?.city" class="cdr-tt-row">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            {{ contactDetail.additional_attributes.city }}<span v-if="contactDetail.additional_attributes.country">, {{ contactDetail.additional_attributes.country }}</span>
          </div>

          <!-- Bio -->
          <div v-if="contactDetail?.additional_attributes?.description" class="cdr-tt-bio">
            "{{ contactDetail.additional_attributes.description }}"
          </div>

          <!-- Redes sociais -->
          <div v-if="ttSocials.length" class="cdr-tt-socials">
            <a
              v-for="s in ttSocials"
              :key="s.key"
              :href="s.url"
              target="_blank"
              rel="noopener"
              class="cdr-tt-social"
              :style="{ background: s.color }"
              :title="s.key"
            >{{ s.label }}</a>
          </div>

          <!-- Canal -->
          <div v-if="contactDetail?.contact_inboxes?.[0]" class="cdr-tt-row">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
            </svg>
            {{ contactDetail.contact_inboxes[0].inbox.name }}
          </div>

          <!-- Time -->
          <div v-if="tt.conv.team" class="cdr-tt-row">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
            {{ tt.conv.team.name }}
          </div>

          <!-- Etiquetas -->
          <div v-if="tt.conv.labels.length" class="cdr-tt-lbls">
            <span v-for="l in tt.conv.labels" :key="l" class="cdr-tt-lbl">{{ l.replace(/_/g, ' ') }}</span>
          </div>

          <!-- Rodapé -->
          <div class="cdr-tt-footer">
            <span class="cdr-tt-dot" :class="statusDot(tt.conv)"></span>
            {{ statusLabel(tt.conv) }}
            <span v-if="tt.conv.unread_count" class="cdr-tt-unread">
              {{ tt.conv.unread_count }} não lida{{ tt.conv.unread_count > 1 ? 's' : '' }}
            </span>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>

  <!-- ══ Painel (lista / chat) ══ -->
  <transition name="cdr-panel">
    <div
      v-if="panelVisible"
      class="cdr-panel"
      :style="{ width: panelWidth + 'px', right: STRIP_W + 'px' }"
    >
      <!-- Handle de redimensionamento -->
      <div class="cdr-resize-handle" @mousedown.prevent="startResize"></div>

      <!-- ── Lista ── -->
      <template v-if="!chatOpen">
        <div class="cdr-head">
          <div class="cdr-head-title">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            Minhas Conversas
            <span class="cdr-head-badge">{{ myConvs.length }}</span>
          </div>

          <!-- Switch auto-abrir -->
          <div
            class="cdr-auto-wrap"
            :class="{ 'cdr-auto-wrap--on': autoOpen }"
            :title="autoOpen
              ? 'Auto-abrir ativo — quando o cliente enviar mensagem o chat abre automaticamente em janela flutuante'
              : 'Auto-abrir inativo — ative para abrir chats automaticamente quando o cliente enviar mensagem'"
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
            </svg>
            <label class="cdr-auto-sw" @click.stop>
              <input type="checkbox" v-model="autoOpen" />
              <span class="cdr-auto-track"></span>
            </label>
          </div>

          <button class="cdr-icon-btn" @click="closePanel">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div v-if="myConvs.length" class="cdr-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            v-model="convSearch"
            type="text"
            class="cdr-search-input"
            placeholder="Buscar conversa…"
          />
          <button v-if="convSearch" class="cdr-search-clear" @click="convSearch = ''" title="Limpar busca">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div v-if="!myConvs.length" class="cdr-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          <p>Nenhuma conversa ativa atribuída a você</p>
        </div>

        <div v-else-if="!filteredMyConvs.length" class="cdr-empty">
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <p>Nenhuma conversa encontrada para "{{ convSearch }}"</p>
        </div>

        <div class="cdr-list" v-else>
          <div
            v-for="conv in filteredMyConvs"
            :key="conv.id"
            class="cdr-item"
            :class="{ selected: selConvId === conv.id }"
            @click="openChat(conv)"
          >
            <!-- Botão janela flutuante -->
            <button class="cdr-float-btn" @click.stop="floatOpen(conv)" title="Abrir em janela flutuante">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <path d="M15 3h6v6"/><path d="M10 14L21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
              </svg>
            </button>
            <div class="cdr-item-av-wrap">
              <div class="cdr-item-av" :style="{ background: avatarColor(conv.contact.name) }">
                <img
                  v-if="conv.contact.thumbnail"
                  :src="conv.contact.thumbnail"
                  :alt="conv.contact.name"
                  style="width:100%;height:100%;object-fit:cover;border-radius:50%;"
                  @error="(e) => (e.target as HTMLImageElement).style.display='none'"
                />
                <span v-else>{{ initials(conv.contact.name) }}</span>
              </div>
              <span class="cdr-status-dot sm" :class="statusDot(conv)"></span>
            </div>
            <div class="cdr-item-body">
              <div class="cdr-item-r1">
                <span
                  v-if="conv.priority && conv.priority !== 'none'"
                  class="cdr-item-prio"
                  :style="{ background: PRIORITY_COLORS[conv.priority as Priority] + '22', color: PRIORITY_COLORS[conv.priority as Priority], borderColor: PRIORITY_COLORS[conv.priority as Priority] + '55' }"
                  :title="PRIORITY_LABELS[conv.priority as Priority]"
                >
                  <span class="cdr-item-prio-dot" :style="{ background: PRIORITY_COLORS[conv.priority as Priority] }"></span>
                  {{ PRIORITY_LABELS[conv.priority as Priority] }}
                </span>
                <span class="cdr-item-name">{{ conv.contact.name }}</span>
                <span class="cdr-item-time">{{ convTime(conv) }}</span>
              </div>
              <div class="cdr-item-r2">
                <span class="cdr-item-preview">{{ previewText(conv.id) }}</span>
                <span v-if="conv.unread_count && !localReadConvIds.has(conv.id)" class="cdr-unread">{{ conv.unread_count > 9 ? '9+' : conv.unread_count }}</span>
              </div>
              <div v-if="conv.labels.length" class="cdr-item-lbls">
                <span v-for="l in conv.labels.slice(0, 3)" :key="l" class="cdr-lbl">{{ l.replace(/_/g, ' ') }}</span>
              </div>
              <div v-if="isAdmin && conv.assignee" class="cdr-item-assignee">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                {{ conv.assignee.name }}
              </div>
              <div v-else-if="isAdmin && !conv.assignee" class="cdr-item-assignee cdr-item-assignee--none">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                  <circle cx="12" cy="7" r="4"/>
                </svg>
                Sem agente
              </div>
              <div v-if="conv.fluxo_id" class="cdr-item-fluxo">{{ fluxoLabel(conv.fluxo_id) }}</div>
            </div>
          </div>
        </div>
      </template>

      <!-- ── Chat ── -->
      <template v-else-if="activeConv">
        <div class="cdr-head">
          <button class="cdr-icon-btn" @click="backToList" title="Voltar">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          <div class="cdr-item-av-wrap" style="margin: 0 3px 0 4px; position: relative; flex-shrink: 0;">
            <div class="cdr-item-av sm2" :style="{ background: avatarColor(activeConv.contact.name) }">
              <img
                v-if="activeConv.contact.thumbnail"
                :src="activeConv.contact.thumbnail"
                :alt="activeConv.contact.name"
                style="width:100%;height:100%;object-fit:cover;border-radius:50%;"
                @error="(e) => (e.target as HTMLImageElement).style.display='none'"
              />
              <span v-else>{{ initials(activeConv.contact.name) }}</span>
            </div>
            <span class="cdr-status-dot sm" :class="statusDot(activeConv)" style="border-color:#BC0C24"></span>
          </div>
          <div class="cdr-chat-hinfo">
            <div class="cdr-chat-hname-row">
              <span class="cdr-chat-hname">{{ activeConv.contact.name }}</span>
              <span v-if="activeConv.contact.phone" class="cdr-chat-hphone">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13 19.79 19.79 0 0 1 1.61 4.44 2 2 0 0 1 3.59 2.24h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.29 6.29l.91-.91a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                {{ fmtPhone(activeConv.contact.phone) }}
                <button
                  type="button"
                  class="cdr-phone-copy-btn"
                  :class="{ 'is-copied': phoneCopied }"
                  @click.stop="copyPhone(activeConv.contact.phone)"
                  :title="phoneCopied ? 'Copiado!' : 'Copiar número'"
                >
                  <svg v-if="phoneCopied" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                  </svg>
                </button>
              </span>
            </div>
            <div v-if="activeConvDocument" class="cdr-chat-hdoc">{{ activeConvDocument.label }}: {{ activeConvDocument.value }}</div>
            <div class="cdr-chat-hsub">
              <span v-if="activeConv.team">{{ activeConv.team.name }} &middot; </span>#{{ activeConv.id }}
              <span v-if="activeConv.fluxo_id" class="cdr-fluxo-tag">{{ fluxoLabel(activeConv.fluxo_id) }}</span>
              <span :class="['cdr-status-lbl', activeConv.status]">{{ convStatusLabel(activeConv.status) }}</span>
              <span v-if="activeSLABadge" :class="['cdr-sla-badge', activeSLABadge.status]">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                </svg>
                <strong>{{ activeSLABadge.time }}</strong>
                <span>{{ activeSLABadge.label }}</span>
              </span>
              <span v-if="activeConv.assignee" class="cdr-agent-tag">
                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                </svg>
                {{ activeConv.assignee.name }}
              </span>
            </div>
          </div>
          <!-- Status dropdown -->
          <div v-if="can('messages.changeStatus')" class="cdr-status-wrap" @click.stop>
            <button class="cdr-icon-btn" :title="`Status: ${convStatusLabel(activeConv.status)}`"
                    @click="cdrStatusMenuOpen = !cdrStatusMenuOpen; cdrSnoozeMode = false">
              <span class="cdr-s-dot" :class="activeConv.status"></span>
            </button>
            <div v-if="cdrStatusMenuOpen" class="cdr-status-menu">
              <template v-if="!cdrSnoozeMode">
                <button v-if="activeConv.status !== 'open'"     class="cdr-sopt" @click="cdrChangeStatus('open')">
                  <span class="cdr-s-dot open"></span> Abrir
                </button>
                <button v-if="activeConv.status !== 'resolved'" class="cdr-sopt" @click="cdrChangeStatus('resolved')">
                  <span class="cdr-s-dot resolved"></span> Resolver
                </button>
                <button v-if="activeConv.status !== 'pending'"  class="cdr-sopt" @click="cdrChangeStatus('pending')">
                  <span class="cdr-s-dot pending"></span> Pendente
                </button>
                <button v-if="can('messages.snooze')" class="cdr-sopt" @click="cdrSnoozeMode = true">
                  <span class="cdr-s-dot snoozed"></span> Adiar…
                </button>
              </template>
              <template v-else>
                <div class="cdr-snz">
                  <div class="cdr-snz-title">Adiar por quanto tempo?</div>
                  <div class="cdr-snz-row">
                    <input type="number" v-model.number="cdrSnoozeH" min="0" max="99" class="cdr-snz-inp" />
                    <span class="cdr-snz-lbl">h</span>
                    <input type="number" v-model.number="cdrSnoozeM" min="0" max="59" class="cdr-snz-inp" />
                    <span class="cdr-snz-lbl">min</span>
                  </div>
                  <div class="cdr-snz-hint">0h 0min = até próxima resposta</div>
                  <div class="cdr-snz-btns">
                    <button class="cdr-snz-cancel"  @click="cdrSnoozeMode = false">Cancelar</button>
                    <button class="cdr-snz-confirm" @click="cdrConfirmSnooze" :disabled="cdrStatusLoading">Confirmar</button>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <button
            v-if="can('contacts.export')"
            class="cdr-icon-btn"
            :disabled="cdrExporting"
            :title="cdrExporting ? 'Exportando…' : 'Exportar conversa'"
            @click="exportActiveConv"
          >
            <svg v-if="!cdrExporting" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            <span v-else class="cdr-spinner" style="width:10px;height:10px;border-width:2px"></span>
          </button>
          <button class="cdr-icon-btn" @click="openMsgSearch" title="Buscar nas mensagens">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </button>
          <button v-if="can('messages.runMacro')" class="cdr-icon-btn" :class="{ 'cdr-icon-btn--active': cdrMacroPanelOpen }" @click="toggleCdrMacroPanel" :title="cdrMacroPanelOpen ? 'Voltar à conversa' : 'Executar macro'">
            <svg v-if="!cdrMacroPanelOpen" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
            <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </button>
          <button class="cdr-icon-btn" @click="closePanel" title="Fechar conversa">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- Etiquetas + Prioridade -->
        <div class="cdr-chat-lbls">

          <!-- Atribuição de agente -->
          <div v-if="can('messages.assignAgent')" class="cdr-agent-wrap" @click.stop>
            <span v-if="currentAssignee" class="cdr-agent-sel" @click.stop="agentMenuOpen = !agentMenuOpen" title="Mudar agente">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              {{ currentAssignee.name }}
            </span>
            <button v-else class="cdr-lbl-add-btn" @click.stop="agentMenuOpen = !agentMenuOpen">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              agente
            </button>
            <div v-if="agentMenuOpen" class="cdr-agent-menu">
              <div class="cdr-agent-search-wrap">
                <input v-model="agentSearch" class="cdr-agent-search" placeholder="Buscar agente…" @click.stop @keydown.stop />
              </div>
              <div v-if="agentLoading" style="padding:12px;display:flex;justify-content:center">
                <div class="cdr-spinner" style="width:14px;height:14px;border-width:2px"></div>
              </div>
              <template v-else>
                <div class="cdr-agent-list">
                  <button class="cdr-agent-opt" :class="{ active: !currentAssignee }" @click.stop="doAssignAgent(null)">
                    <div class="cdr-agent-av" style="background:var(--ink-10);color:var(--ink-50);font-size:10px">—</div>
                    Sem agente
                    <svg v-if="!currentAssignee" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                  <button v-for="a in filteredAgents" :key="a.id" class="cdr-agent-opt" :class="{ active: currentAssignee?.id === a.id }" @click.stop="doAssignAgent(a.id)">
                    <div class="cdr-agent-av-wrap" :title="availLabel(agentStatusOf(a.id))">
                      <div class="cdr-agent-av" :style="{ background: avatarColor(a.name), overflow:'hidden' }">
                        <img v-if="a.thumbnail" :src="a.thumbnail" :alt="a.name" style="width:100%;height:100%;object-fit:cover;border-radius:50%;display:block" @error="(e) => (e.target as HTMLImageElement).style.display='none'" />
                        <span v-else>{{ initials(a.name) }}</span>
                      </div>
                      <span class="cdr-agent-status-dot" :class="`casd-${agentStatusOf(a.id)}`"></span>
                    </div>
                    {{ a.name }}
                    <svg v-if="currentAssignee?.id === a.id" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                </div>
              </template>
            </div>
          </div>

          <!-- Atribuição de time -->
          <div v-if="canAssignTeam" class="cdr-agent-wrap" @click.stop>
            <span v-if="currentTeam" class="cdr-agent-sel" @click.stop="cdrTeamMenuOpen = !cdrTeamMenuOpen" title="Mudar time">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              {{ currentTeam.name }}
            </span>
            <button v-else class="cdr-lbl-add-btn" @click.stop="cdrTeamMenuOpen = !cdrTeamMenuOpen">
              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
              time
            </button>
            <div v-if="cdrTeamMenuOpen" class="cdr-agent-menu">
              <div class="cdr-agent-search-wrap">
                <input v-model="cdrTeamSearch" class="cdr-agent-search" placeholder="Buscar time…" @click.stop @keydown.stop />
              </div>
              <div v-if="cdrTeamsLoading" style="padding:12px;display:flex;justify-content:center">
                <div class="cdr-spinner" style="width:14px;height:14px;border-width:2px"></div>
              </div>
              <template v-else>
                <div class="cdr-agent-list">
                  <button class="cdr-agent-opt" :class="{ active: !currentTeam }" @click.stop="doAssignCdrTeam(null)">
                    <div class="cdr-agent-av" style="background:var(--ink-10);color:var(--ink-50);font-size:10px">—</div>
                    Sem time
                    <svg v-if="!currentTeam" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                  <button v-for="t in filteredCdrTeams" :key="t.id" class="cdr-agent-opt" :class="{ active: currentTeam?.id === t.id }" @click.stop="doAssignCdrTeam(t.id)">
                    <div class="cdr-agent-av" :style="{ background: avatarColor(t.name) }">{{ initials(t.name) }}</div>
                    {{ t.name }}
                    <svg v-if="currentTeam?.id === t.id" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
                  </button>
                </div>
              </template>
            </div>
          </div>

          <!-- Tag de prioridade -->
          <div v-if="can('messages.setPriority')" class="cdr-priority-wrap" @click.stop>
            <span
              v-if="currentPriority"
              class="cdr-priority-tag"
              :style="{ background: PRIORITY_COLORS[currentPriority] + '20', color: PRIORITY_COLORS[currentPriority], borderColor: PRIORITY_COLORS[currentPriority] + '55' }"
              @click.stop="priorityMenuOpen = !priorityMenuOpen"
              :title="`Prioridade: ${PRIORITY_LABELS[currentPriority]}`"
            >
              <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor" style="flex-shrink:0">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
              </svg>
              {{ PRIORITY_LABELS[currentPriority] }}
              <span v-if="priorityLoading" class="cdr-p-spin"></span>
            </span>
            <button
              v-else
              class="cdr-lbl-add-btn cdr-priority-add"
              @click.stop="priorityMenuOpen = !priorityMenuOpen"
              :disabled="priorityLoading"
            >
              <svg v-if="!priorityLoading" width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
              </svg>
              <span v-else class="cdr-p-spin"></span>
              prioridade
            </button>
            <div v-if="priorityMenuOpen" class="cdr-priority-menu">
              <button
                v-for="p in (['urgent','high','medium','low','none'] as const)"
                :key="p"
                class="cdr-priority-opt"
                :class="{ active: currentPriority === p || (!currentPriority && p === 'none') }"
                @click.stop="doTogglePriority(p)"
              >
                <span v-if="p !== 'none'" class="cdr-priority-dot" :style="{ background: PRIORITY_COLORS[p] }"></span>
                <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                {{ PRIORITY_LABELS[p] }}
                <svg v-if="currentPriority === p || (!currentPriority && p === 'none')" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e;flex-shrink:0"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
            </div>
          </div>

          <span v-for="l in currentLabels" :key="l" class="cdr-lbl cdr-lbl-active">
            {{ l.replace(/_/g, ' ') }}
            <button v-if="!isLabelProtected(l)" class="cdr-lbl-x" @click.stop="toggleLabel(l)" :disabled="updatingLabels">×</button>
            <svg v-else class="cdr-lbl-lock" width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" title="Etiqueta protegida — não pode ser removida">
              <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
          </span>
          <div class="cdr-lbl-picker">
            <button class="cdr-lbl-add-btn" @click.stop="labelsOpen = !labelsOpen" :disabled="updatingLabels">
              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              etiqueta
            </button>
            <div v-if="labelsOpen" class="cdr-lbl-dropdown">
              <div v-if="!allLabels.length" class="cdr-lbl-loading">
                <span class="cdr-spinner" style="width:14px;height:14px;border-width:2px;"></span>
              </div>
              <label
                v-for="lbl in allLabels"
                :key="lbl.id"
                class="cdr-lbl-opt"
                :class="{ checked: currentLabels.includes(lbl.title) }"
                @click.prevent="(currentLabels.includes(lbl.title) && isLabelProtected(lbl.title)) || toggleLabel(lbl.title)"
              >
                <span class="cdr-lbl-dot" :style="{ background: lbl.color }"></span>
                <span class="cdr-lbl-opt-name">{{ lbl.title.replace(/_/g, ' ') }}</span>
                <svg v-if="currentLabels.includes(lbl.title) && isLabelProtected(lbl.title)" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:var(--ink-40);flex-shrink:0" title="Etiqueta protegida — não pode ser removida">
                  <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <svg v-else-if="currentLabels.includes(lbl.title)" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="margin-left:auto;color:#22c55e;flex-shrink:0">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </label>
            </div>
          </div>
        </div>

        <!-- Snooze banner -->
        <div v-if="activeConv.status === 'snoozed'" class="cdr-snooze-banner">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <span v-if="cdrSnoozeRemainingLabel">Adiado — {{ cdrSnoozeRemainingLabel }} restantes</span>
          <span v-else>Adiado até próxima resposta do cliente</span>
        </div>

        <!-- Banner: janela de 24h de mensagens fechada -->
        <div v-if="cdrWindowClosed" class="cdr-window-banner">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>Você só pode responder a esta conversa usando um template devido à janela de mensagens de 24 horas do WhatsApp</span>
        </div>

        <!-- Busca nas mensagens -->
        <div v-if="msgSearchOpen" class="cdr-msg-search-bar">
          <input
            ref="msgSearchInputEl"
            v-model="msgSearchQuery"
            class="cdr-msg-search-input"
            placeholder="Buscar nas mensagens…"
            @keydown.enter="doMsgSearch"
            @keydown.escape="closeMsgSearch"
          />
          <span v-if="msgSearchActive" class="cdr-msg-search-count">
            {{ msgMatchIds.length ? (msgCurrentIdx + 1) + '/' + msgMatchIds.length : '0 resultados' }}
          </span>
          <button v-if="msgMatchIds.length" class="cdr-msg-search-nav" @click="goPrevMsg" title="Anterior">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="18 15 12 9 6 15"/></svg>
          </button>
          <button v-if="msgMatchIds.length" class="cdr-msg-search-nav" @click="goNextMsg" title="Próximo">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
          <button class="cdr-msg-search-close" @click="closeMsgSearch">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <!-- Painel de Macros -->
        <div v-if="cdrMacroPanelOpen" class="cdr-macro-panel">

          <!-- Formulário inline -->
          <template v-if="cdrMacroFormTarget && !cdrMacroRunning && !cdrMacroRunDone">
            <div class="cdr-macro-head">
              <button class="cdr-macro-back" @click="cdrMacroFormTarget = null">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                Voltar
              </button>
              <span class="cdr-macro-run-title">{{ cdrMacroFormTarget.name }}</span>
            </div>
            <div class="cdr-macro-form">
              <div v-for="(fa, cardIdx) in cdrMacroFormActions" :key="fa.idx" class="cdr-mform-card">
                <div class="cdr-mform-card-header">
                  <span class="cdr-mform-num">{{ cardIdx + 1 }}</span>
                  <div class="cdr-mform-card-info">
                    <span class="cdr-mform-card-title">Webhook com Formulário</span>
                    <span v-if="fa.url" class="cdr-mform-card-url">{{ fa.url }}</span>
                  </div>
                </div>
                <p class="cdr-mform-hint">Preencha os campos antes de executar</p>
                <div v-for="field in fa.fields" :key="field.key" class="cdr-mform-field">
                  <label class="cdr-mform-label">{{ field.label || field.key }}</label>
                  <input
                    class="cdr-mform-input"
                    v-model="cdrMacroFormValues[fa.idx][field.key]"
                    :placeholder="field.label || field.key"
                    type="text"
                  />
                </div>
              </div>
              <div class="cdr-mform-btns">
                <button class="cdr-mform-submit" @click="submitCdrMacroForm">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                  Executar
                </button>
              </div>
            </div>
          </template>

          <!-- Lista de macros -->
          <template v-else-if="!cdrMacroRunning && !cdrMacroRunDone">
            <div class="cdr-macro-head">
              <div class="cdr-macro-search-wrap">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input v-model="cdrMacroSearch" class="cdr-macro-search" placeholder="Buscar macro…" />
              </div>
              <span class="cdr-macro-count">{{ cdrFilteredMacros.length }} macro{{ cdrFilteredMacros.length !== 1 ? 's' : '' }}</span>
            </div>
            <div v-if="cdrMacrosLoading" class="cdr-macro-loading">
              <span class="cdr-spinner"></span>
            </div>
            <div v-else class="cdr-macro-list">
              <button
                v-for="m in cdrFilteredMacros"
                :key="m.id"
                class="cdr-macro-item"
                @click="runCdrMacro(m)"
              >
                <div class="cdr-macro-item-body">
                  <span class="cdr-macro-item-name">{{ m.name }}</span>
                  <span class="cdr-macro-item-sub">{{ m.actions.length }} ação{{ m.actions.length !== 1 ? 'ões' : '' }}</span>
                </div>
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
              </button>
              <div v-if="!cdrFilteredMacros.length && !cdrMacrosLoading" class="cdr-macro-empty">Nenhuma macro encontrada</div>
            </div>
          </template>

          <!-- Resultado da execução -->
          <template v-else>
            <div class="cdr-macro-head">
              <button v-if="cdrMacroRunDone" class="cdr-macro-back" @click="resetCdrMacroRun">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                Voltar
              </button>
              <span class="cdr-macro-run-title">{{ cdrMacroRunningObj?.name }}</span>
              <span v-if="cdrMacroRunning" class="cdr-spinner" style="width:12px;height:12px;border-width:2px;flex-shrink:0"></span>
            </div>
            <div class="cdr-macro-steps">
              <div
                v-for="(step, i) in cdrMacroStepResults"
                :key="i"
                class="cdr-macro-step"
                :class="step.ok ? 'cdr-macro-step--ok' : 'cdr-macro-step--err'"
              >
                <svg v-if="step.ok" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <svg v-else width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                <span class="cdr-macro-step-label">{{ step.label }}</span>
                <span v-if="!step.ok" class="cdr-macro-step-msg">{{ step.msg }}</span>
              </div>
            </div>
            <div v-if="cdrMacroRunDone" class="cdr-macro-summary" :class="cdrMacroStepResults.some(s => !s.ok) ? 'cdr-macro-summary--warn' : 'cdr-macro-summary--ok'">
              <svg v-if="!cdrMacroStepResults.some(s => !s.ok)" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              {{ cdrMacroStepResults.some(s => !s.ok) ? cdrMacroStepResults.filter(s => !s.ok).length + ' ação(ões) com erro' : 'Concluído com sucesso!' }}
            </div>
          </template>
        </div>

        <!-- Mensagens -->
        <div v-show="!cdrMacroPanelOpen" class="cdr-msgs" ref="msgsEl" @scroll="onMsgsScroll">
          <div v-if="loadingMsgs || msgSearchLoading" class="cdr-msgs-loading">
            <template v-if="msgSearchLoading">
              <span class="cdr-spinner"></span>
              <span class="cdr-msgs-loading-label">Buscando em toda a conversa…</span>
            </template>
            <!-- Carregamento inicial (sem cache ainda, ver openChat()) — esqueleto de
                 bolhas no lugar do spinner central, mesmo padrão do PLANO-SKELETON-DASHBOARD.md -->
            <div v-else class="cdr-msg-skeleton">
              <div class="cdr-sk-row inn"><div class="cdr-sk-bubble" style="width:58%"></div></div>
              <div class="cdr-sk-row inn"><div class="cdr-sk-bubble" style="width:36%"></div></div>
              <div class="cdr-sk-row out"><div class="cdr-sk-bubble" style="width:46%"></div></div>
              <div class="cdr-sk-row inn"><div class="cdr-sk-bubble" style="width:64%"></div></div>
              <div class="cdr-sk-row out"><div class="cdr-sk-bubble" style="width:30%"></div></div>
            </div>
          </div>
          <template v-else>
            <div v-if="loadingMore" class="cdr-load-more-row">
              <span class="cdr-spinner-sm"></span>
            </div>
            <div v-else-if="noMoreMsgs" class="cdr-no-more">início da conversa</div>
            <div
              v-for="msg in processedMsgs"
              :key="msg.id"
              class="cdr-msg-row"
              :class="{ out: msg.message_type === 1, inn: msg.message_type === 0, act: msg.message_type === 2, 'msg-match-current': msgSearchActive && msgMatchIds[msgCurrentIdx] === msg.id }"
              :data-msg-id="msg.id"
            >
              <!-- Mensagem de atividade -->
              <div v-if="msg.message_type === 2" class="cdr-msg-act">{{ msg.content }}</div>

              <template v-else>
                <!-- Avatar do remetente (só na última mensagem do grupo) -->
                <div
                  v-if="msg.showAvatar"
                  class="cdr-msg-av"
                  :style="{ background: avatarColor(senderName(msg)) }"
                  :title="senderName(msg)"
                >
                  <img
                    v-if="msg.sender?.thumbnail && msg.message_type === 1"
                    :src="msg.sender.thumbnail"
                    :alt="senderName(msg)"
                    @error="(e) => (e.target as HTMLImageElement).style.display='none'"
                  />
                  <span v-else>{{ initials(senderName(msg)) }}</span>
                </div>
                <!-- Espaço reservado quando avatar está oculto (mantém alinhamento) -->
                <div v-else class="cdr-msg-av-spacer"></div>

                <div class="cdr-bubble" :class="{ 'cdr-bubble--priv': msg.private }">
                  <span v-if="msg.private" class="cdr-priv-tag">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                    Nota interna
                  </span>
                  <span v-if="msg.content" class="cdr-bubble-text" v-html="highlightMsgHtml(msg.content, msg.message_type === 1, authUser?.message_signature)"></span>
                  <!-- Botão de download para mensagens de arquivo enviado via n8n -->
                  <a
                    v-if="msg.message_type === 1 && extractFileKey(msg.content || '')"
                    :href="n8nDownloadUrl(extractFileKey(msg.content || ''))"
                    target="_blank"
                    rel="noopener"
                    class="cdr-file-dl-btn"
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Baixar arquivo
                  </a>
                  <!-- Anexos -->
                  <template v-if="msg.attachments?.length">
                    <div v-for="att in msg.attachments" :key="att.id" class="cdr-att">
                      <!-- Imagem -->
                      <a v-if="att.file_type === 'image'" :href="att.data_url" target="_blank" rel="noopener" class="cdr-att-img-wrap">
                        <img :src="att.thumb_url || att.data_url" class="cdr-att-img" alt="imagem" loading="lazy" />
                      </a>
                      <!-- Áudio — player customizado -->
                      <div v-else-if="att.file_type === 'audio'" class="cdr-ap">
                        <button class="cdr-ap-btn" @click="toggleAudio(att)">
                          <!-- Pause -->
                          <svg v-if="audioState[att.id]?.playing" width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/>
                          </svg>
                          <!-- Play -->
                          <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="5 3 19 12 5 21 5 3"/>
                          </svg>
                        </button>
                        <div class="cdr-ap-mid">
                          <div class="cdr-ap-bar" @click.stop>
                            <div class="cdr-ap-fill" :style="{ width: audioProgress(att.id) + '%' }"></div>
                            <input type="range" class="cdr-ap-seek" min="0" max="100" step="0.1"
                                   :value="audioProgress(att.id)"
                                   @input="seekAudio(att, $event)" />
                          </div>
                          <div class="cdr-ap-time">
                            <span>{{ fmtTime(audioState[att.id]?.current || 0) }}</span>
                            <span>{{ fmtTime(audioState[att.id]?.duration || 0) }}</span>
                          </div>
                        </div>
                        <a :href="att.data_url" target="_blank" rel="noopener" class="cdr-ap-dl" title="Baixar">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                        </a>
                      </div>
                      <!-- Vídeo -->
                      <div v-else-if="isVideo(att)" class="cdr-att-video-wrap">
                        <video :src="att.data_url" controls preload="metadata" class="cdr-att-video"></video>
                      </div>
                      <!-- PDF -->
                      <a v-else-if="isPdf(att)" :href="att.data_url" target="_blank" rel="noopener" class="cdr-att-file cdr-att-pdf">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                          <line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/>
                        </svg>
                        <div class="cdr-att-file-info">
                          <span class="cdr-att-file-ext">PDF</span>
                          <span class="cdr-att-file-size">{{ fmtSize(att.file_size) }}</span>
                        </div>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                      </a>
                      <!-- Arquivo genérico -->
                      <a v-else :href="att.data_url" target="_blank" rel="noopener" class="cdr-att-file">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                        </svg>
                        <div class="cdr-att-file-info">
                          <span class="cdr-att-file-ext">{{ att.extension?.toUpperCase() || 'Arquivo' }}</span>
                          <span class="cdr-att-file-size">{{ fmtSize(att.file_size) }}</span>
                        </div>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                      </a>
                    </div>
                  </template>
                  <span class="cdr-bubble-ts">
                    {{ fmtTs(msg.created_at) }}
                    <span v-if="msg.message_type === 1" class="cdr-msg-status" :class="'s-'+(msg.status||'sent')" :title="msg.status === 'read' ? 'Lido' : msg.status === 'delivered' ? 'Entregue' : msg.status === 'failed' ? 'Falhou' : 'Enviado'">
                      <svg v-if="msg.status === 'failed'" width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><line x1="1" y1="1" x2="7" y2="7"/><line x1="7" y1="1" x2="1" y2="7"/></svg>
                      <svg v-else-if="msg.status === 'read' || msg.status === 'delivered'" width="14" height="8" viewBox="0 0 14 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="1,4 3.5,6.5 9,1"/><polyline points="5,4 7.5,6.5 13,1"/></svg>
                      <svg v-else width="10" height="8" viewBox="0 0 10 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="1,4 3.5,6.5 9,1"/></svg>
                    </span>
                  </span>
                </div>
              </template>
            </div>
          </template>
        </div>

        <!-- Pré-visualização de arquivos -->
        <div v-if="files.length && !cdrMacroPanelOpen" class="cdr-files">
          <div v-for="(f, i) in files" :key="i" class="cdr-file-pill">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
            <span class="cdr-file-name">{{ f.name }}</span>
            <button class="cdr-file-rm" @click="files.splice(i, 1)">×</button>
          </div>
        </div>

        <!-- Compositor — normal -->
        <div v-if="!recording && !recordedBlob && !videoRecording && !videoRecordedBlob && !cdrMacroPanelOpen" class="cdr-composer" :class="{ 'cdr-composer--priv': notaInterna }">
          <label class="cdr-attach-btn" :class="{ 'cdr-attach-disabled': cdrWindowClosed || notaInterna }" title="Anexar arquivo">
            <input type="file" multiple hidden ref="fileInputEl" :disabled="cdrWindowClosed || notaInterna" @change="onFilePick" />
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
            </svg>
          </label>
          <button
            v-if="can('messages.privateNote')"
            class="cdr-priv-btn"
            :class="{ 'cdr-priv-btn--active': notaInterna }"
            :disabled="cdrWindowClosed"
            @click="notaInterna = !notaInterna"
            :title="notaInterna ? 'Nota interna ativada — clique para voltar ao modo normal' : 'Marcar como nota interna (não visível pro cliente)'"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </button>
          <EmojiPicker v-if="!cdrWindowClosed" @pick="insertEmoji" />
          <textarea
            ref="taEl"
            v-model="draft"
            class="cdr-ta"
            :placeholder="notaInterna ? 'Nota interna — só a equipe vê… (Enter envia)' : (cdrWindowClosed ? 'Envie um template para responder…' : 'Mensagem… (Enter envia)')"
            :disabled="cdrWindowClosed"
            rows="1"
            @keydown.enter.exact.prevent="sendMsg"
            @input="growTa"
          ></textarea>
          <!-- Nota interna: só textarea + enviar, sem template/mic/câmera (não fazem sentido pra uma nota privada) -->
          <!-- Fora da janela de 24h: só template. Senão, mic + câmera quando não há nada pra enviar; Send caso contrário -->
          <template v-if="!notaInterna && (cdrWindowClosed || (!canSend && !sending))">
            <button v-if="can('messages.sendTemplate')" class="cdr-wa-tpl-btn" @click="openCdrTplModal" title="Enviar template WhatsApp">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </button>
            <template v-if="!cdrWindowClosed">
              <button class="cdr-mic-btn" @click="startRecording" title="Gravar áudio">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              </button>
              <button class="cdr-mic-btn" @click="startVideoRecording" title="Gravar vídeo">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7"/>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
              </button>
            </template>
          </template>
          <button v-else class="cdr-send-btn" @click="sendMsg" :disabled="!canSend || sending">
            <span v-if="sending" class="cdr-spinner-sm-white"></span>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>

        <!-- Compositor — gravando -->
        <div v-else-if="recording && !cdrMacroPanelOpen" class="cdr-recording">
          <button class="cdr-rec-cancel" @click="cancelRecording" title="Cancelar gravação">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <span class="cdr-rec-dot"></span>
          <div class="cdr-rec-wave">
            <div v-for="(h, i) in waveformBars" :key="i" class="cdr-rec-bar" :style="{ height: h + 'px' }"></div>
          </div>
          <span class="cdr-rec-time">{{ fmtRecordingTime(recordingTime) }}</span>
          <button class="cdr-rec-stop" @click="stopRecording" title="Parar e revisar">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <rect x="4" y="4" width="16" height="16" rx="2"/>
            </svg>
          </button>
        </div>

        <!-- Compositor — preview da gravação -->
        <div v-else-if="recordedBlob && !cdrMacroPanelOpen" class="cdr-rec-preview">
          <button class="cdr-rec-cancel" @click="cancelRecording" title="Descartar">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
          <audio :src="recordedUrl" controls preload="auto" class="cdr-rec-audio"></audio>
          <button class="cdr-rec-send" @click="sendRecordedAudio" title="Enviar áudio">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>

        <!-- Compositor — gravando vídeo -->
        <div v-else-if="videoRecording && !cdrMacroPanelOpen" class="cdr-vrec-wrap">
          <video ref="videoPreviewEl" muted autoplay playsinline class="cdr-vrec-preview"></video>
          <div class="cdr-vrec-bar">
            <button class="cdr-rec-cancel" @click="cancelVideoRecording" title="Cancelar gravação">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <span class="cdr-rec-dot"></span>
            <span class="cdr-rec-time" style="flex:1; text-align:left;">{{ fmtRecordingTime(videoRecordingTime) }}</span>
            <button class="cdr-rec-stop" @click="stopVideoRecording" title="Parar e revisar">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <rect x="4" y="4" width="16" height="16" rx="2"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Compositor — preview do vídeo -->
        <div v-else-if="videoRecordedBlob && !cdrMacroPanelOpen" class="cdr-vrec-wrap">
          <video :src="videoRecordedUrl" controls preload="auto" class="cdr-vrec-preview"></video>
          <div class="cdr-vrec-bar">
            <button class="cdr-rec-cancel" @click="cancelVideoRecording" title="Descartar">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <span style="flex:1; font-size:12px; color:#6b7280;">Pronto para enviar</span>
            <button class="cdr-rec-send" @click="sendRecordedVideo" title="Enviar vídeo">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        </div>
      </template>
    </div>
  </transition>

  <!-- ── Modal: Enviar Template WhatsApp ── -->
  <Teleport to="body">
    <div v-if="cdrTplOpen" class="cdr-tpl-overlay" @mousedown.self="closeCdrTplModal">
      <div class="cdr-tpl-dialog">
        <div class="cdr-tpl-head">
          <div class="cdr-tpl-title">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zm-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zm8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            Enviar Template WhatsApp
            <span class="cdr-tpl-contact-name">→ {{ activeConv?.contact.name }}</span>
          </div>
          <button class="cdr-tpl-close" @click="closeCdrTplModal">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="cdr-tpl-body">
          <!-- Lista de templates -->
          <div class="cdr-tpl-list-col">
            <div class="cdr-tpl-col-lbl">Templates aprovados</div>
            <div v-if="cdrTplsLoading" class="cdr-tpl-loading"><span class="cdr-tpl-spin"></span> Carregando…</div>
            <div v-else-if="!cdrTplsApproved.length" class="cdr-tpl-loading">Nenhum template aprovado</div>
            <div v-else class="cdr-tpl-items">
              <button v-for="t in cdrTplsApproved" :key="t.id"
                class="cdr-tpl-item" :class="{ active: cdrTplSel?.id === t.id }"
                @click="selectCdrTpl(t)">
                <div class="cdr-tpl-item-name">{{ t.name.replace(/_/g,' ') }}</div>
                <div class="cdr-tpl-item-meta">
                  <span class="cdr-tpl-cat" :class="t.category.toLowerCase()">{{ t.category === 'MARKETING' ? 'Marketing' : 'Utilidade' }}</span>
                  <span class="cdr-tpl-lang">{{ t.language }}</span>
                </div>
              </button>
            </div>
          </div>
          <!-- Preview + variáveis -->
          <div class="cdr-tpl-config-col">
            <div v-if="!cdrTplSel" class="cdr-tpl-pick-hint">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/></svg>
              <span>Selecione um template ao lado</span>
            </div>
            <template v-else>
              <div class="cdr-tpl-col-lbl" style="margin-bottom:8px">Preview em tempo real</div>
              <div class="cdr-tpl-preview-wrap">
                <div class="cdr-tpl-bubble">
                  <div v-if="cdrTplHasImg" class="cdr-tpl-bubble-img">
                    <img v-if="cdrTplImgUrl && !cdrTplImgErr" :src="cdrTplImgUrl" alt="" @error="cdrTplImgErr = true" />
                    <div v-else class="cdr-tpl-img-ph"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg></div>
                  </div>
                  <div v-if="cdrTplHeaderTxt" class="cdr-tpl-bubble-hdr">{{ cdrTplHeaderTxt }}</div>
                  <div class="cdr-tpl-bubble-body" v-html="cdrTplPreviewHtml"></div>
                  <div v-if="cdrTplFooterTxt" class="cdr-tpl-bubble-ftr">{{ cdrTplFooterTxt }}</div>
                  <div class="cdr-tpl-bubble-time">{{ cdrTplTime }}</div>
                </div>
                <div v-if="cdrTplButtons.length" class="cdr-tpl-btns">
                  <div v-for="(b,i) in cdrTplButtons" :key="i" class="cdr-tpl-btn">{{ b.text || 'Botão' }}</div>
                </div>
              </div>
              <div v-if="cdrTplHasImg" style="margin-top:10px">
                <div class="cdr-tpl-field-lbl">URL da imagem</div>
                <input type="url" class="cdr-tpl-input" v-model="cdrTplImgUrl" placeholder="https://…" @input="cdrTplImgErr = false" />
                <div class="cdr-tpl-hint">Use uma URL pública permanente</div>
              </div>
              <div v-if="cdrTplVarNums.length" style="margin-top:12px">
                <div class="cdr-tpl-col-lbl" style="margin-bottom:6px">Variáveis</div>
                <div v-for="n in cdrTplVarNums" :key="n" style="margin-bottom:7px">
                  <div class="cdr-tpl-field-lbl">Variável &#123;&#123;{{ n }}&#125;&#125;</div>
                  <div v-if="cdrTplVarMapping[n]" class="cdr-tpl-mapped-badge">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                    Preenchido automaticamente: {{ cdrMappedLabel(n) }}
                  </div>
                  <input v-else type="text" class="cdr-tpl-input" :placeholder="`Valor para {{${n}}}`" :value="cdrTplVars[n] || ''" @input="setCdrTplVar(n, ($event.target as HTMLInputElement).value)" />
                </div>
              </div>
              <div style="margin-top:16px">
                <div v-if="cdrTplSendBlocked" class="cdr-tpl-blocked-hint">{{ cdrTplSendBlockedReason }}</div>
                <button
                  class="cdr-tpl-send-btn"
                  :disabled="cdrTplSending || cdrTplSendBlocked"
                  :title="cdrTplSendBlockedReason"
                  @click="sendCdrTpl"
                >
                  <span v-if="!cdrTplSending">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:5px"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                    Enviar para {{ activeConv?.contact.name }}
                  </span>
                  <span v-else class="cdr-tpl-spin" style="border-top-color:#fff;border-color:rgba(255,255,255,.3)"></span>
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onUnmounted, reactive } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import type { Conversation } from '@/types/dashboard'
import { avatarColor, initials, fluxoLabel, conversationDocument, isFluxoGeral, confirmAssignDuringFluxoGeral, confirmResolveConversation } from '@/composables/useHelpers'
import { fmtPhone } from '@/utils/contactsFormat'
import { pendingChatId, currentOpenConvId, closeDrawerSignal, focusReplySignal, openMyConvsSignal, activeConvContext } from '@/composables/useChatTrigger'
import { useFloatChats } from '@/composables/useFloatChats'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { useAgentChat } from '@/composables/useAgentChat'
import { usePermission } from '@/composables/usePermission'
import { useSLA } from '@/composables/useSLA'
import { useConversationStore } from '@/composables/useConversationStore'
import { useMyConversations } from '@/composables/useMyConversations'
import { fetchConversationById } from '@/services/conversationService'
import Swal from 'sweetalert2'
import EmojiPicker from '@/components/dashboard/EmojiPicker.vue'
import { n8nDownloadUrl } from '@/api/n8n'
import { snoozeConversation } from '@/services/snoozeService'
import { listMacros, type MacroAction } from '@/services/macrosService'
import { listTeams, listTeamMembersOrAgents, type TeamItem } from '@/services/teamsService'
import {
  toggleConversationStatus, assignAgentViaSystem, assignTeam, logManualAssignment,
  fetchAccountLabels, setConversationLabels, fetchConversation,
} from '@/services/conversationActionsService'
import { runMacroAction, resolveConversationSmart, type MacroActionContext } from '@/services/macroEngineService'
import { isLabelProtected } from '@/composables/useProtectedLabels'
import { fetchMessages, fetchOlderMessages } from '@/services/messagesService'
import { useMessageCache } from '@/composables/useMessageCache'
import { onConversationMessage } from '@/composables/useChatwootMessageRealtime'
import { useMessageSearch } from '@/composables/useMessageSearch'
import { getFileType, isVideo, isPdf, fmtSize, extractFileKey } from '@/composables/useMediaHelpers'
import { useAudioRecorder } from '@/composables/useAudioRecorder'
import { useWhatsappTemplates } from '@/composables/useWhatsappTemplates'
import { useMessageSender } from '@/composables/useMessageSender'
import { useMessageWindow } from '@/composables/useMessageWindow'
import { CHATWOOT_BASE, authHeaders } from '@/api/chatwoot'
import { fetchConversationMessagesForExportApi } from '@/services/contactsService'
import { buildConvHtml, downloadHtmlFile } from '@/utils/contactsExport'
import type { ChatMessage, ProcessedMsg, LabelItem, AgentItem, Priority, SLABadge } from '@/types/chat'
import { PRIORITY_LABELS, PRIORITY_COLORS } from '@/types/chat'
import { allAgents } from '@/composables/useDashboardEngine'
import { playNotificationSound } from '@/composables/useNotificationSound'

const { open: floatOpen } = useFloatChats()
const { mode: railMode, toggle: toggleRailMode } = useChatRailMode()
const { totalUnread: agentChatUnread } = useAgentChat()

interface ContactDetail {
  id: number
  name: string
  email?: string
  phone_number?: string
  thumbnail?: string
  availability_status?: string
  additional_attributes?: {
    city?: string
    country?: string
    description?: string
    company_name?: string
    social_profiles?: {
      instagram?: string
      twitter?: string
      facebook?: string
      linkedin?: string
      telegram?: string
      tiktok?: string
      github?: string
    }
  }
  contact_inboxes?: Array<{
    source_id: string
    inbox: { id: number; name: string; channel_type: string; avatar_url?: string }
  }>
}

const store       = useStore()
const route       = useRoute()
const authUser    = computed(() => store.getters.authUser)
const isAdmin     = computed(() => store.getters.isAdmin)
// O Modo Foco (/foco) já cobre "minhas conversas" na própria página — o rail flutuante
// fica escondido lá pra não ter duas fontes de "conversa atual" competindo na tela.
const hiddenOnFoco = computed(() => route.name === 'foco')

const { can, allowedTeamIds } = usePermission()

/* ── Conversations (internal fetch) ──────── */
const { conversations } = useConversationStore()



/* ── Auto-abrir + badge local ────────────── */
const autoOpen         = ref(localStorage.getItem('cdr_auto_open') === 'true')
const prevUnreadMap    = reactive<Record<number, number>>({})
const localReadConvIds = reactive<Set<number>>(new Set())

watch(autoOpen, (val) => {
  localStorage.setItem('cdr_auto_open', val ? 'true' : 'false')
  conversations.value.forEach(c => { prevUnreadMap[c.id] = c.unread_count })
})

watch(conversations, (newConvs) => {
  newConvs.forEach(conv => {
    const tracked = conv.id in prevUnreadMap
    const prev    = prevUnreadMap[conv.id] ?? conv.unread_count

    // Novo mensagem do cliente: contagem aumentou
    if (tracked && conv.unread_count > prev) {
      localReadConvIds.delete(conv.id)           // badge volta a aparecer
      if (autoOpen.value && myConvs.value.some(c => c.id === conv.id)) {
        floatOpen(conv)
      }
    }

    // Servidor zerou unread (ex: agente enviou via Chatwoot): limpa local também
    if (conv.unread_count === 0) {
      localReadConvIds.delete(conv.id)
    }

    prevUnreadMap[conv.id] = conv.unread_count
  })
}, { deep: true })

/* ── Status change state ─────────────────── */
const cdrStatusMenuOpen = ref(false)
const cdrSnoozeMode     = ref(false)
const cdrSnoozeH        = ref(1)
const cdrSnoozeM        = ref(0)
const cdrStatusLoading  = ref(false)
const cdrNowSec         = ref(Math.floor(Date.now() / 1000))

/* ── State ───────────────────────────────── */
const listOpen    = ref(false)
const chatOpen    = ref(false)
const selConvId   = ref<number | null>(null)
// Singleton de módulo (useMessageCache.ts) — compartilhado com FocoView.vue,
// MobileView.vue e FloatChatWindow.vue, pra mesma conversa aberta em mais de
// um lugar não buscar o histórico 2x.
const { msgCache } = useMessageCache()
const loadingMsgs = ref(false)
const loadingMore = ref(false)
const noMoreMsgs  = ref(false)
const draft       = ref('')
const files       = ref<File[]>([])
const notaInterna = ref(false)
const msgsEl      = ref<HTMLElement | null>(null)
const taEl        = ref<HTMLTextAreaElement | null>(null)
const fileInputEl = ref<HTMLInputElement | null>(null)
const stripListEl = ref<HTMLElement | null>(null)

/* ── Scroll do trilho de avatares (setas) ──────────────────────────
   A lista já era scrollável (overflow-y: auto), só não tinha nenhum indício
   visual disso — com mais conversas do que cabem no trilho, dava pra achar
   que só as visíveis existiam (mesmo padrão de AgentChatDrawer.vue). */
const canScrollUp   = ref(false)
const canScrollDown = ref(false)

function updateStripScrollState() {
  const el = stripListEl.value
  if (!el) { canScrollUp.value = false; canScrollDown.value = false; return }
  canScrollUp.value   = el.scrollTop > 4
  canScrollDown.value = el.scrollTop + el.clientHeight < el.scrollHeight - 4
}

function scrollStrip(dir: 1 | -1) {
  stripListEl.value?.scrollBy({ top: dir * 120, behavior: 'smooth' })
}

/* ── Prioridade ───────────────────────────── */
const priorityMenuOpen = ref(false)
const priorityLoading  = ref(false)
const convPriority     = reactive<Record<number, Priority>>({})

const currentPriority = computed<Priority | null>(() => {
  if (!selConvId.value) return null
  const local = convPriority[selConvId.value]
  if (local !== undefined) return local === 'none' ? null : local
  const p = activeConv.value?.priority
  return (p && p !== 'none') ? (p as Priority) : null
})

async function doTogglePriority(priority: Priority) {
  if (!selConvId.value || priorityLoading.value) return
  const convId = selConvId.value
  priorityMenuOpen.value = false
  priorityLoading.value = true
  const prev = convPriority[convId]
  convPriority[convId] = priority
  try {
    const res = await fetch(`${API}/conversations/${convId}/toggle_priority`, {
      method: 'POST',
      headers: { ...apiHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ priority }),
    })
    if (!res.ok) {
      if (prev !== undefined) convPriority[convId] = prev
      else delete convPriority[convId]
    }
  } catch (e) {
    console.error('[ConvDrawer] priority:', e)
    if (prev !== undefined) convPriority[convId] = prev
    else delete convPriority[convId]
  } finally {
    priorityLoading.value = false
  }
}

function onPriorityDocClick(e: MouseEvent) {
  const el = document.querySelector('.cdr-priority-wrap')
  if (el && !el.contains(e.target as Node)) priorityMenuOpen.value = false
}

watch(priorityMenuOpen, open => {
  if (open) setTimeout(() => document.addEventListener('click', onPriorityDocClick), 0)
  else document.removeEventListener('click', onPriorityDocClick)
})

/* ── Atribuição de agente ───────────────────────────── */
const agentMenuOpen  = ref(false)
const agentLoading   = ref(false)
const teamAgents     = ref<AgentItem[]>([])
const agentSearch    = ref('')
const convAssignee   = reactive<Record<number, { id: number; name: string; thumbnail: string } | null>>({})
let   _agentsFetched = false

// Status (agent_status) pra bolinha no avatar da lista — mesma fonte usada em
// TeamMembersModal.vue, não o availability_status do Chatwoot (ver useAvailability.ts).
const agentStatusByUser = ref<Record<number, string>>({})

const filteredAgents = computed(() => {
  const q = agentSearch.value.toLowerCase().trim()
  return q ? teamAgents.value.filter(a => a.name.toLowerCase().includes(q)) : teamAgents.value
})

const currentAssignee = computed(() => {
  if (!selConvId.value) return null
  const convId = selConvId.value
  if (convId in convAssignee) return convAssignee[convId]
  return activeConv.value?.assignee ?? null
})

function agentStatusOf(userId: number): string {
  return agentStatusByUser.value[userId] ?? 'offline'
}

function availLabel(status: string): string {
  return { online: 'Online', busy: 'Ocupado', offline: 'Offline' }[status] ?? 'Offline'
}

// Traz todos os agentes da conta (não só os do time da conversa) — o supervisor
// pode precisar atribuir a qualquer agente, independente do time atual.
// Status de disponibilidade vem de allAgents (useDashboardEngine) — já mantido
// por loadData() a cada ciclo (login/Atualizar/fim do countdown); list_agent_status
// não é mais chamado à parte aqui (ver useDashboardEngine.loadData()).
async function fetchAgents() {
  if (agentLoading.value || _agentsFetched) return
  _agentsFetched = true
  agentLoading.value = true
  try {
    teamAgents.value = await listTeamMembersOrAgents(authUser.value?.access_token || '', null)
    agentStatusByUser.value = Object.fromEntries(
      allAgents.value.map(a => [a.id, a.availability_status])
    )
  } catch {
    _agentsFetched = false
  } finally {
    agentLoading.value = false
  }
}

async function doAssignAgent(agentId: number | null) {
  if (!selConvId.value) return
  const convId = selConvId.value
  if (agentId && isFluxoGeral(activeConv.value?.fluxo_id)) {
    if (!(await confirmAssignDuringFluxoGeral())) return
  }
  agentMenuOpen.value = false
  agentSearch.value = ''
  const agent = agentId ? teamAgents.value.find(a => a.id === agentId) : null
  const prev = convAssignee[convId] !== undefined ? convAssignee[convId] : (activeConv.value?.assignee ?? null)
  convAssignee[convId] = agent ? { id: agent.id, name: agent.name, thumbnail: agent.thumbnail } : null
  const idx = conversations.value.findIndex(c => c.id === convId)
  if (idx !== -1) conversations.value[idx] = {
    ...conversations.value[idx],
    assignee: agent ? { id: agent.id, name: agent.name, email: '', role: 'agent', thumbnail: agent.thumbnail, availability: 'online' } : null,
  }
  try {
    const ok = await assignAgentViaSystem(convId, agentId)
    if (!ok) {
      convAssignee[convId] = prev
      if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], assignee: (prev as any) }
    } else if (agent && idx !== -1 && authUser.value) {
      logManualAssignment(convId, agent.id, agent.name, conversations.value[idx].contact.name, authUser.value.id, authUser.value.name)
    }
  } catch {
    convAssignee[convId] = prev
    if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], assignee: (prev as any) }
  }
}

function onAgentDocClick() { agentMenuOpen.value = false; agentSearch.value = '' }
watch(agentMenuOpen, open => {
  if (open) { fetchAgents(); setTimeout(() => document.addEventListener('click', onAgentDocClick), 0) }
  else document.removeEventListener('click', onAgentDocClick)
})

/* ── Atribuição de time ───────────────────── */
const cdrTeamMenuOpen  = ref(false)
const cdrTeamsLoading  = ref(false)
const cdrTeamsList     = ref<TeamItem[]>([])
const cdrTeamSearch    = ref('')
const convTeam         = reactive<Record<number, TeamItem | null>>({})
let   _cdrTeamsFetched = false

const canAssignTeam = computed(() => can('messages.assignTeam'))

const filteredCdrTeams = computed(() => {
  const allowed = allowedTeamIds()
  let list = allowed !== null ? cdrTeamsList.value.filter(t => allowed.includes(t.id)) : cdrTeamsList.value
  const q = cdrTeamSearch.value.toLowerCase().trim()
  return q ? list.filter(t => t.name.toLowerCase().includes(q)) : list
})

const currentTeam = computed<TeamItem | null>(() => {
  if (!selConvId.value) return null
  const convId = selConvId.value
  if (convId in convTeam) return convTeam[convId]
  return activeConv.value?.team ?? null
})

async function fetchCdrTeams() {
  if (_cdrTeamsFetched) return
  cdrTeamsLoading.value = true
  try {
    cdrTeamsList.value = await listTeams(authUser.value?.access_token || '')
    _cdrTeamsFetched = true
  } catch { /* silencioso */ } finally {
    cdrTeamsLoading.value = false
  }
}

async function doAssignCdrTeam(teamId: number | null) {
  if (!selConvId.value) return
  const convId = selConvId.value
  cdrTeamMenuOpen.value = false
  cdrTeamSearch.value = ''
  const team = teamId ? cdrTeamsList.value.find(t => t.id === teamId) : null
  const prev = convTeam[convId] !== undefined ? convTeam[convId] : (activeConv.value?.team ?? null)
  convTeam[convId] = team ?? null
  const idx = conversations.value.findIndex(c => c.id === convId)
  if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], team: team ?? null }
  try {
    const ok = await assignTeam(convId, authUser.value?.access_token || '', teamId)
    if (!ok) {
      convTeam[convId] = prev
      if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], team: (prev as any) }
    }
  } catch {
    convTeam[convId] = prev
    if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], team: (prev as any) }
  }
}

function onCdrTeamDocClick() { cdrTeamMenuOpen.value = false; cdrTeamSearch.value = '' }
watch(cdrTeamMenuOpen, open => {
  if (open) { fetchCdrTeams(); setTimeout(() => document.addEventListener('click', onCdrTeamDocClick), 0) }
  else document.removeEventListener('click', onCdrTeamDocClick)
})

/* ── Etiquetas ───────────────────────────── */
const allLabels      = ref<LabelItem[]>([])
const labelsOpen     = ref(false)
const updatingLabels = ref(false)
const convLabels     = reactive<Record<number, string[]>>({})

/* Tooltip via JS (evita clipping do overflow-y: auto) */
const tt = ref<{ conv: Conversation; y: number } | null>(null)

/* ── Contact detail cache ────────────────── */
const contactCache   = reactive<Record<number, ContactDetail>>({})
const contactLoading = ref(0)

const contactDetail = computed<ContactDetail | null>(() => {
  const id = tt.value?.conv?.contact?.id
  return id ? (contactCache[id] ?? null) : null
})

const ttSocials = computed(() => {
  const sp = contactDetail.value?.additional_attributes?.social_profiles
  if (!sp) return []
  const MAP: Record<string, { label: string; color: string }> = {
    instagram: { label: 'IG', color: '#e1306c' },
    twitter:   { label: 'TW', color: '#1da1f2' },
    facebook:  { label: 'FB', color: '#1877f2' },
    linkedin:  { label: 'LI', color: '#0a66c2' },
    telegram:  { label: 'TG', color: '#0088cc' },
    tiktok:    { label: 'TK', color: '#010101' },
    github:    { label: 'GH', color: '#333' },
  }
  return Object.entries(sp)
    .filter(([, url]) => !!url)
    .map(([key, url]) => ({ ...MAP[key], key, url: url as string }))
    .filter(s => s.label)
})

/* ── Audio player + gravação de áudio ────── */
const {
  audioState, toggleAudio, seekAudio, audioProgress, fmtTime, disposeAudioEls,
  recording, recordingTime, recordedBlob, recordedUrl, waveformBars,
  startRecording, stopRecording, cancelRecording, sendRecordedAudio, fmtRecordingTime,
} = useAudioRecorder({
  barCount: 28,
  exclusivePlayback: true,
  onSend: async (file) => { files.value = [file]; await sendMsg() },
})

/* ── Gravação de vídeo ───────────────────── */
const videoRecording     = ref(false)
const videoRecordingTime = ref(0)
const videoRecordedBlob  = ref<Blob | null>(null)
const videoRecordedUrl   = ref('')
const videoPreviewEl     = ref<HTMLVideoElement | null>(null)
let _vmr: MediaRecorder | null = null
let _vstream: MediaStream | null = null
let _vrecTimer: ReturnType<typeof setInterval> | null = null

async function startVideoRecording() {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true })
    _vstream = stream
    const chunks: Blob[] = []
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
      ? 'video/webm;codecs=vp9,opus'
      : MediaRecorder.isTypeSupported('video/webm') ? 'video/webm' : 'video/mp4'
    _vmr = new MediaRecorder(stream, { mimeType })
    _vmr.ondataavailable = e => { if (e.data.size > 0) chunks.push(e.data) }
    _vmr.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType })
      videoRecordedBlob.value = blob
      if (videoRecordedUrl.value) URL.revokeObjectURL(videoRecordedUrl.value)
      videoRecordedUrl.value = URL.createObjectURL(blob)
      stream.getTracks().forEach(t => t.stop())
    }
    _vmr.start(100)
    videoRecording.value = true
    videoRecordingTime.value = 0
    _vrecTimer = setInterval(() => videoRecordingTime.value++, 1000)
    await nextTick()
    if (videoPreviewEl.value) videoPreviewEl.value.srcObject = stream
  } catch {
    Swal.fire({ title: 'Câmera inacessível', text: 'Não foi possível acessar a câmera. Verifique as permissões do navegador.', icon: 'error' })
  }
}

function stopVideoRecording() {
  _vmr?.stop()
  videoRecording.value = false
  if (_vrecTimer) { clearInterval(_vrecTimer); _vrecTimer = null }
}

function cancelVideoRecording() {
  if (videoRecording.value) {
    if (_vmr) { _vmr.onstop = null; _vmr.stop() }
    _vstream?.getTracks().forEach(t => t.stop())
    videoRecording.value = false
    if (_vrecTimer) { clearInterval(_vrecTimer); _vrecTimer = null }
  }
  videoRecordedBlob.value = null
  if (videoRecordedUrl.value) { URL.revokeObjectURL(videoRecordedUrl.value); videoRecordedUrl.value = '' }
}

async function sendRecordedVideo() {
  if (!videoRecordedBlob.value) return
  const ext = videoRecordedBlob.value.type.includes('mp4') ? 'mp4' : 'webm'
  const file = new File([videoRecordedBlob.value], `video_${Date.now()}.${ext}`, { type: videoRecordedBlob.value.type })
  videoRecordedBlob.value = null
  if (videoRecordedUrl.value) { URL.revokeObjectURL(videoRecordedUrl.value); videoRecordedUrl.value = '' }
  files.value = [file]
  await sendMsg()
}

const STRIP_W = 60
const MIN_W   = 260
const MAX_W   = 640
const panelWidth = ref(Math.max(MIN_W, Math.min(MAX_W, parseInt(localStorage.getItem('cdr_panel_w') || '320'))))

const API = `${CHATWOOT_BASE}/api/v1/accounts/3`

const {
  tplOpen: cdrTplOpen, tplsLoading: cdrTplsLoading, tplsApproved: cdrTplsApproved,
  tplSel: cdrTplSel, tplVarNums: cdrTplVarNums, tplVars: cdrTplVars, tplVarMapping: cdrTplVarMapping, tplImgUrl: cdrTplImgUrl,
  tplImgErr: cdrTplImgErr, tplSending: cdrTplSending, tplTime: cdrTplTime,
  tplHasImg: cdrTplHasImg, tplHeaderTxt: cdrTplHeaderTxt, tplFooterTxt: cdrTplFooterTxt,
  tplButtons: cdrTplButtons, tplPreviewHtml: cdrTplPreviewHtml,
  tplSendBlocked: cdrTplSendBlocked, tplSendBlockedReason: cdrTplSendBlockedReason,
  openTplModal: openCdrTplModal, closeTplModal: closeCdrTplModal,
  selectTpl: selectCdrTpl, setTplVar: setCdrTplVar, sendTpl: sendCdrTpl, mappedLabel: cdrMappedLabel,
} = useWhatsappTemplates({
  getContactPhone: () => activeConv.value?.contact?.phone,
  isWindowClosed:  () => cdrWindowClosed.value,
  getContact:      () => activeConv.value?.contact,
})

function apiHeaders(): Record<string, string> {
  return authHeaders(authUser.value?.access_token || '')
}

function apiUrl(path: string): string {
  return `${API}${path}?api_access_token=${authUser.value?.access_token || ''}`
}

/* ── Macros ──────────────────────────────── */
interface CdrMacroItem { id: string; name: string; actions: MacroAction[]; team_ids: number[]; is_active: boolean }

const cdrMacroPanelOpen   = ref(false)
const cdrMacrosList       = ref<CdrMacroItem[]>([])
const cdrMacrosLoading    = ref(false)
const cdrMacroSearch      = ref('')
const cdrMacroRunning     = ref(false)
const cdrMacroRunDone     = ref(false)
const cdrMacroRunningObj  = ref<CdrMacroItem | null>(null)
const cdrMacroStepResults = ref<{ label: string; ok: boolean; msg: string }[]>([])
const cdrMacroFormTarget  = ref<CdrMacroItem | null>(null)
const cdrMacroFormValues  = ref<Record<number, Record<string, string>>>({})

const cdrMacroFormActions = computed(() => {
  if (!cdrMacroFormTarget.value) return []
  return cdrMacroFormTarget.value.actions
    .map((action, idx) => ({ action, idx }))
    .filter(({ action }) => action.action_name === 'send_webhook_event_form')
    .map(({ action, idx }) => {
      const cfg = action.action_params?.[0] ?? {}
      const fields: Array<{ key: string; label: string }> = Array.isArray(cfg.fields) ? cfg.fields : []
      const url = typeof cfg === 'object' ? String(cfg.url ?? '') : ''
      return { idx, fields, url }
    })
    .filter(({ fields }) => fields.length > 0)
})

const cdrFilteredMacros = computed(() => {
  const q          = cdrMacroSearch.value.trim().toLowerCase()
  const convTeamId = activeConv.value?.team?.id ?? null
  const active     = cdrMacrosList.value.filter(m => {
    if (!m.is_active) return false
    if (!m.team_ids?.length) return true
    return convTeamId ? m.team_ids.includes(convTeamId) : true
  })
  return q ? active.filter(m => m.name.toLowerCase().includes(q)) : active
})

async function loadCdrMacros() {
  if (cdrMacrosList.value.length) return
  cdrMacrosLoading.value = true
  try {
    cdrMacrosList.value = await listMacros(authUser.value?.access_token || '')
  } catch { /* silencioso */ } finally {
    cdrMacrosLoading.value = false
  }
}

function toggleCdrMacroPanel() {
  cdrMacroPanelOpen.value = !cdrMacroPanelOpen.value
  if (cdrMacroPanelOpen.value) {
    loadCdrMacros()
    resetCdrMacroRun()
  }
}

function resetCdrMacroRun() {
  cdrMacroRunning.value     = false
  cdrMacroRunDone.value     = false
  cdrMacroRunningObj.value  = null
  cdrMacroStepResults.value = []
  cdrMacroFormTarget.value  = null
  cdrMacroFormValues.value  = {}
}

/**
 * Contexto pro motor de macro. Sempre parte dos dados já carregados no drawer;
 * se a macro tiver alguma ação de webhook, faz um GET da conversa pra ter
 * inbox_id/contact_email atualizados e a conversa bruta (fallback de corpo vazio).
 */
async function cdrMacroContext(macro: CdrMacroItem): Promise<MacroActionContext> {
  const conv = activeConv.value
  const base: MacroActionContext = {
    contactPhone:       conv?.contact?.phone ?? null,
    contactName:        conv?.contact?.name  ?? null,
    contactEmail:       (conv?.contact as any)?.email ?? null,
    teamId:             conv?.team?.id   ?? null,
    teamName:           conv?.team?.name ?? null,
    conversationStatus: conv?.status ?? null,
    contactId:          conv?.contact?.id ?? null,
    assigneeId:         conv?.assignee?.id ?? null,
    assigneeName:       conv?.assignee?.name ?? null,
    labels:             conv?.labels ?? [],
    accountId:          authUser.value?.account_id ?? null,
    macroId:            macro.id,
    macroName:          macro.name,
    executedById:       authUser.value?.id ?? null,
    executedByName:     authUser.value?.name ?? null,
  }
  const needsLiveContext = macro.actions.some(a => a.action_name === 'send_webhook_event' || a.action_name === 'send_webhook_event_form')
  if (!needsLiveContext || !conv) return base
  try {
    const data = await fetchConversation(conv.id, authUser.value?.access_token || '')
    if (!data) return base
    return {
      ...base,
      contactEmail: data?.meta?.sender?.email ?? base.contactEmail,
      inboxId:      data?.inbox_id ?? null,
      inboxName:    data?.channel ?? null,
      rawConversation: data,
    }
  } catch {
    return base
  }
}

async function runCdrMacro(macro: CdrMacroItem) {
  if (!activeConv.value) return

  const formActions = macro.actions
    .map((a, idx) => ({ action: a, idx }))
    .filter(({ action }) => action.action_name === 'send_webhook_event_form')
    .filter(({ action }) => {
      const cfg = action.action_params?.[0] ?? {}
      return Array.isArray(cfg.fields) && cfg.fields.length > 0
    })

  if (formActions.length) {
    const vals: Record<number, Record<string, string>> = {}
    for (const { action, idx } of formActions) {
      const cfg = action.action_params?.[0] ?? {}
      const fields: Array<{ key: string; label: string }> = Array.isArray(cfg.fields) ? cfg.fields : []
      vals[idx] = {}
      for (const field of fields) vals[idx][field.key] = ''
    }
    cdrMacroFormValues.value = vals
    cdrMacroFormTarget.value = macro
    return
  }

  await executeCdrMacro(macro, {})
}

async function submitCdrMacroForm() {
  if (!cdrMacroFormTarget.value) return
  const macro = cdrMacroFormTarget.value
  const formValuesByIdx = { ...cdrMacroFormValues.value }
  cdrMacroFormTarget.value = null
  await executeCdrMacro(macro, formValuesByIdx)
}

async function executeCdrMacro(macro: CdrMacroItem, formValuesByIdx: Record<number, Record<string, string>>) {
  if (!activeConv.value) return
  const convId = activeConv.value.id
  cdrMacroRunningObj.value  = macro
  cdrMacroRunning.value     = true
  cdrMacroRunDone.value     = false
  cdrMacroStepResults.value = []
  const context = await cdrMacroContext(macro)
  for (const [idx, action] of macro.actions.entries()) {
    const formVals = action.action_name === 'send_webhook_event_form'
      ? (formValuesByIdx[idx] ?? {})
      : undefined
    cdrMacroStepResults.value.push(
      await runMacroAction(action, convId, authUser.value?.access_token || '', context, formVals)
    )
  }
  cdrMacroRunning.value = false
  cdrMacroRunDone.value = true
}

/* ── Computed ────────────────────────────── */
const { myConvs } = useMyConversations()

const panelVisible = computed(() => (listOpen.value || chatOpen.value) && railMode.value === 'client' && !hiddenOnFoco.value)

watch(myConvs, () => nextTick(updateStripScrollState), { flush: 'post' })

// Busca/filtro por nome ou telefone do contato na lista "Minhas Conversas" —
// não mexe na ordem de myConvs, só filtra o que é exibido.
const convSearch = ref('')
const filteredMyConvs = computed(() => {
  const q = convSearch.value.trim().toLowerCase()
  if (!q) return myConvs.value
  return myConvs.value.filter(c =>
    c.contact.name.toLowerCase().includes(q) || c.contact.phone.toLowerCase().includes(q)
  )
})
watch(panelVisible, open => { if (!open) convSearch.value = '' })

const activeConv = computed(() =>
  selConvId.value != null ? myConvs.value.find(c => c.id === selConvId.value) ?? null : null
)

watch([activeConv, chatOpen], ([conv, open]) => {
  activeConvContext.value = open && conv ? { id: conv.id, fluxoId: conv.fluxo_id, cnpj: conv.cnpj } : null
})

const { getProfileForFluxo: getSLAProfile } = useSLA()

const activeConvDocument = computed(() => activeConv.value ? conversationDocument(activeConv.value) : null)

const activeSLABadge = computed<SLABadge | null>(() => {
  const conv = activeConv.value
  if (!conv || (conv.status !== 'open' && conv.status !== 'pending')) return null
  const profile = getSLAProfile(conv.fluxo_id)
  if (!profile) return null
  const now = cdrNowSec.value
  const candidates: { remaining: number; label: string; status: 'breached' | 'atRisk' }[] = []

  if (profile.resolutionTime > 0) {
    const deadline = conv.created_at + profile.resolutionTime * 60
    const remaining = deadline - now
    if (remaining <= 0) candidates.push({ remaining, label: 'Resolução', status: 'breached' })
    else if (profile.notifyBefore > 0 && remaining <= profile.notifyBefore * 60)
      candidates.push({ remaining, label: 'Resolução', status: 'atRisk' })
  }
  if (profile.firstResponseTime > 0 && conv.waiting_since > 0) {
    const deadline = conv.waiting_since + profile.firstResponseTime * 60
    const remaining = deadline - now
    if (remaining <= 0) candidates.push({ remaining, label: '1ª Resposta', status: 'breached' })
    else if (profile.notifyBefore > 0 && remaining <= profile.notifyBefore * 60)
      candidates.push({ remaining, label: '1ª Resposta', status: 'atRisk' })
  }
  if (!candidates.length) return null
  candidates.sort((a, b) => a.remaining - b.remaining)
  const worst = candidates[0]
  const abs = Math.abs(Math.floor(worst.remaining))
  const h = Math.floor(abs / 3600), m = Math.floor((abs % 3600) / 60), s = abs % 60
  const timeStr = worst.remaining <= 0
    ? `+${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : s + 's'}`
    : `${h > 0 ? h + 'h ' : ''}${m > 0 ? m + 'min' : s + 's'}`
  return { label: worst.label, time: timeStr, status: worst.status }
})

const cdrSnoozeRemainingLabel = computed<string | null>(() => {
  const conv = activeConv.value
  if (!conv || conv.status !== 'snoozed' || !conv.snoozed_until) return null
  const diff = conv.snoozed_until - cdrNowSec.value
  if (diff <= 0) return null
  const h = Math.floor(diff / 3600)
  const m = Math.floor((diff % 3600) / 60)
  if (h > 0) return `${h}h ${m > 0 ? m + 'min' : ''}`
  if (m > 0) return `${m}min`
  return 'menos de 1min'
})

const processedMsgs = computed<ProcessedMsg[]>(() => {
  if (!selConvId.value) return []
  const msgs = (msgCache.value[selConvId.value] || [])
    .filter(m => m.message_type <= 2 && (m.content || m.attachments?.length))
  return msgs.map((msg, i) => {
    const next = msgs[i + 1]
    // mostra avatar na última mensagem de cada grupo de remetente consecutivo
    const showAvatar = !next || next.message_type !== msg.message_type
    return { ...msg, showAvatar }
  })
})

const canSend = computed(() => draft.value.trim().length > 0 || files.value.length > 0)

const { isWindowClosed: cdrWindowClosed } = useMessageWindow(processedMsgs, cdrNowSec)

// Notas internas exigem a janela aberta (mesmo composer da mensagem normal) — se a janela
// fechar enquanto o toggle está ativo, desativa pra não deixar o composer num estado inválido.
watch(cdrWindowClosed, (closed) => { if (closed) notaInterna.value = false })

/* ── Message search ── */
const {
  msgSearchOpen, msgSearchQuery, msgSearchActive, msgSearchLoading, msgCurrentIdx, msgSearchInputEl,
  msgMatchIds, getSig, highlightMsgHtml,
  openMsgSearch, closeMsgSearch, doMsgSearch, goNextMsg, goPrevMsg,
} = useMessageSearch({
  processedMsgs,
  msgsEl,
  messageSignature: () => authUser.value?.message_signature || '',
  loadMoreMsgs,
  noMoreMsgs,
})

const currentLabels = computed<string[]>(() => {
  if (!selConvId.value) return []
  return convLabels[selConvId.value] ?? activeConv.value?.labels ?? []
})

/* ── Tooltip ─────────────────────────────── */
function showTT(conv: Conversation, e: MouseEvent) {
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  tt.value = { conv, y: Math.round(rect.top + rect.height / 2) }
  if (conv.contact.id && !contactCache[conv.contact.id]) fetchContactDetail(conv.contact.id)
}
function hideTT() { tt.value = null }

async function fetchContactDetail(id: number) {
  if (contactCache[id] || contactLoading.value === id) return
  contactLoading.value = id
  try {
    const res = await fetch(apiUrl(`/contacts/${id}`), { headers: apiHeaders() })
    if (!res.ok) return
    const data = await res.json()
    contactCache[id] = data.payload
  } catch { /* silencioso */ } finally {
    if (contactLoading.value === id) contactLoading.value = 0
  }
}

/* ── Panel control ───────────────────────── */
function toggleList() {
  if (chatOpen.value) { chatOpen.value = false; listOpen.value = true; return }
  listOpen.value = !listOpen.value
  if (listOpen.value) prefetchPreviews()
}

function closePanel() {
  listOpen.value = false
  chatOpen.value = false
  selConvId.value = null
  currentOpenConvId.value = null
}

function backToList() {
  chatOpen.value = false
  listOpen.value = true
}

const cdrExporting = ref(false)

async function exportActiveConv() {
  const conv = activeConv.value
  if (!conv || cdrExporting.value) return
  cdrExporting.value = true
  try {
    const messages = await fetchConversationMessagesForExportApi(authUser.value?.access_token || '', conv.id)
    const convForExport = {
      id:               conv.id,
      status:           conv.status,
      created_at:       conv.created_at,
      last_activity_at: conv.resolved_at,
      meta: {
        sender:   conv.contact,
        assignee: conv.assignee,
        team:     conv.team,
        channel:  conv.channel,
      },
    }
    const html = buildConvHtml(convForExport, messages, conv.contact.name)
    downloadHtmlFile(html, `conversa-${conv.id}-${new Date().toISOString().slice(0, 10)}.html`)
  } catch (e: any) {
    Swal.fire({ title: 'Erro ao exportar', text: e.message || 'Não foi possível buscar as mensagens.', icon: 'error' })
  } finally {
    cdrExporting.value = false
  }
}

function selectConv(conv: Conversation) {
  hideTT()
  if (selConvId.value === conv.id && chatOpen.value) { closePanel(); return }
  openChat(conv)
}

async function openChat(conv: Conversation) {
  localReadConvIds.add(conv.id)   // limpa badge imediatamente
  selConvId.value = conv.id
  listOpen.value  = false
  chatOpen.value  = true
  convLabels[conv.id] = [...conv.labels]
  labelsOpen.value = false
  fetchAllLabels()
  // Reabrir uma conversa já vista nesta sessão usa fetchMsgs em modo silencioso
  // (sem "loadingMsgs = true", sem spinner de tela cheia cobrindo o que já tem
  // em msgCache) — processedMsgs já é reativo a selConvId+msgCache, então o
  // histórico cacheado aparece na hora, e o fetch só faz merge por cima. Só a
  // primeira abertura de uma conversa (sem cache ainda) usa silent=false.
  // fetchMsgs decide se rola pro fim sozinho só quando NÃO é silencioso — pra
  // não depender do scroll da conversa anterior ainda na tela nesse instante,
  // força o scroll pro fim aqui depois, cobrindo os dois casos.
  const hadCache = !!msgCache.value[conv.id]
  await fetchMsgs(conv.id, hadCache)
  if (selConvId.value === conv.id) { await nextTick(); scrollBottom() }
}

async function openById(id: number) {
  let conv = conversations.value.find(c => c.id === id)
  if (!conv) {
    // Conversa fora do escopo já carregado localmente (ex.: resultado da busca global
    // apontando pra uma conversa resolvida há tempo, ou de um time não listado agora) —
    // busca avulsa igual ao fluxo de "conversa nova via WebSocket" em useDashboardEngine.ts.
    const fetched = await fetchConversationById(id, authUser.value?.access_token || '')
    if (!fetched) {
      Swal.fire({ title: 'Conversa não encontrada', text: `Não foi possível abrir a conversa #${id}.`, icon: 'error' })
      return
    }
    conversations.value = [...conversations.value, fetched]
    conv = fetched
  }
  await openChat(conv)
}

watch(pendingChatId, async (id) => {
  if (id === null) return
  pendingChatId.value = null
  // Força o trilho pro modo "cliente" — sem isso o popup fica montado mas invisível
  // quando o agente estava no modo "agente" (AgentChatDrawer) ou o strip inteiro
  // escondido (cdr-strip só renderiza com railMode === 'client', ver topo do template).
  railMode.value = 'client'
  currentOpenConvId.value = id
  await openById(id)
})

watch(closeDrawerSignal, (val) => {
  if (val > 0 && (chatOpen.value || listOpen.value)) closePanel()
})

watch(focusReplySignal, (val) => {
  if (val > 0 && chatOpen.value) nextTick(() => taEl.value?.focus())
})

// Atalho "C" — mesmo efeito de clicar no ícone "Minhas Conversas" do rail; se o rail
// estiver no modo "agente", troca pra "cliente" e já abre a lista em vez de só alternar.
watch(openMyConvsSignal, (val) => {
  if (val === 0) return
  if (railMode.value !== 'client') {
    railMode.value = 'client'
    listOpen.value = true
    prefetchPreviews()
  } else {
    toggleList()
  }
})

/* ── Labels API ──────────────────────────── */
async function fetchAllLabels() {
  if (allLabels.value.length) return
  try {
    allLabels.value = await fetchAccountLabels(authUser.value?.access_token || '')
  } catch { /* silencioso */ }
}

async function toggleLabel(label: string) {
  if (!selConvId.value || updatingLabels.value) return
  const convId = selConvId.value
  const current = [...(convLabels[convId] ?? activeConv.value?.labels ?? [])]
  const next = current.includes(label) ? current.filter(l => l !== label) : [...current, label]
  updatingLabels.value = true
  try {
    const result = await setConversationLabels(convId, authUser.value?.access_token || '', next)
    if (result) convLabels[convId] = result
  } catch (e) {
    console.error('[ConvDrawer] labels:', e)
  } finally {
    updatingLabels.value = false
  }
}

function onLblDocClick(e: MouseEvent) {
  const el = document.querySelector('.cdr-lbl-picker')
  if (el && !el.contains(e.target as Node)) labelsOpen.value = false
}

watch(labelsOpen, open => {
  if (open) setTimeout(() => document.addEventListener('click', onLblDocClick), 0)
  else document.removeEventListener('click', onLblDocClick)
})

/* ── Fetch messages ──────────────────────── */
// Merge por id (não substitui o array inteiro): uma atualização silenciosa
// (poll de 8s/evento realtime) só traz o lote recente da API — sobrescrever
// o cache com isso descartava o histórico já carregado via loadMoreMsgs e
// resetava a posição de quem tava lendo mensagens antigas. Ordena de novo
// por segurança (created_at), já que o merge não garante ordem.
function mergeMsgs(existing: ChatMessage[], incoming: ChatMessage[]): ChatMessage[] {
  const byId = new Map(existing.map(m => [m.id, m]))
  incoming.forEach(m => byId.set(m.id, m))
  return [...byId.values()].sort((a, b) => a.created_at - b.created_at)
}

// "Perto do fim" = auto-scroll é esperado (chat normal); acima disso, quem
// tá lendo histórico não pode ser puxado pro fim por uma atualização em segundo plano.
function isNearBottom(): boolean {
  const el = msgsEl.value
  if (!el) return true
  return el.scrollHeight - el.scrollTop - el.clientHeight < 120
}

async function fetchMsgs(convId: number, silent = true) {
  if (!silent) loadingMsgs.value = true
  noMoreMsgs.value = false
  const wasNearBottom = isNearBottom()
  try {
    const messages = await fetchMessages(convId)
    if (messages !== null) {
      // Toca o som só quando já havia cache pra essa conversa (evita disparar
      // pra cada mensagem do histórico na primeira abertura) e a mensagem nova
      // é do contato (message_type 0 = incoming, 1 = outgoing, 2 = atividade).
      const prevMsgs = msgCache.value[convId]
      if (prevMsgs) {
        const prevIds = new Set(prevMsgs.map(m => m.id))
        if (messages.some(m => m.message_type === 0 && !prevIds.has(m.id))) playNotificationSound()
      }
      msgCache.value = {
        ...msgCache.value,
        [convId]: silent && prevMsgs ? mergeMsgs(prevMsgs, messages) : messages,
      }
    }
  } catch (e) {
    console.error('[ConvDrawer]', e)
  } finally {
    loadingMsgs.value = false
    if (convId === selConvId.value && (!silent || wasNearBottom)) {
      await nextTick()
      scrollBottom()
    }
  }
}

// Evento realtime (message.created/updated da conversa aberta) — usa a
// mensagem que já vem no próprio evento (ver useChatwootMessageRealtime.ts)
// e faz merge direto no cache, sem golpe de rede. O poll de 8s continua
// existindo como rede de segurança (via fetchMsgs acima, já não-destrutivo).
function onRealtimeMsg(convId: number, message: ChatMessage) {
  const prevMsgs = msgCache.value[convId]
  if (!prevMsgs) { fetchMsgs(convId, true); return } // sem cache ainda (raro) — cai pro fetch normal
  const isNewIncoming = message.message_type === 0 && !prevMsgs.some(m => m.id === message.id)
  const wasNearBottom = isNearBottom()
  msgCache.value = { ...msgCache.value, [convId]: mergeMsgs(prevMsgs, [message]) }
  if (isNewIncoming) playNotificationSound()
  if (convId === selConvId.value && wasNearBottom) nextTick(() => scrollBottom())
}

async function loadMoreMsgs() {
  const convId = selConvId.value
  if (!convId || loadingMore.value || noMoreMsgs.value) return
  const current = msgCache.value[convId] ?? []
  if (!current.length) return
  loadingMore.value = true
  const prevHeight = msgsEl.value?.scrollHeight ?? 0
  try {
    const result = await fetchOlderMessages(convId, current)
    if (result.noMore) { noMoreMsgs.value = true; return }
    if (result.messages === current) return
    msgCache.value = { ...msgCache.value, [convId]: result.messages }
    await nextTick()
    if (msgsEl.value) msgsEl.value.scrollTop = msgsEl.value.scrollHeight - prevHeight
  } catch (e) {
    console.error('[ConvDrawer loadMore]', e)
  } finally {
    loadingMore.value = false
  }
}

function onMsgsScroll() {
  if (msgsEl.value && msgsEl.value.scrollTop < 60) loadMoreMsgs()
}

async function prefetchPreviews() {
  for (const conv of myConvs.value) {
    if (!msgCache.value[conv.id]) await fetchMsgs(conv.id, true)
  }
}

/* ── Send ────────────────────────────────── */
const { sending, sendMsg } = useMessageSender({
  getConvId: () => selConvId.value,
  getToken: () => authUser.value?.access_token || '',
  getContactPhone: () => activeConv.value?.contact?.phone,
  getSig,
  draft,
  files,
  isPrivate: notaInterna,
  fileInputEl,
  taEl,
  scrollBottom,
  getAssigneeId: () => currentAssignee.value?.id ?? null,
  getMyAgentId: () => authUser.value?.id ?? 0,
  getLabels: () => currentLabels.value,
  getStatus: () => activeConv.value?.status,
  onMessageCreated: (msg, convId) => {
    if (!msgCache.value[convId]) msgCache.value[convId] = []
    msgCache.value[convId] = [...msgCache.value[convId], msg]
  },
  onSent: (convId) => localReadConvIds.add(convId),  // agente enviou → limpa badge
  onLabelsChanged: (convId, labels) => { convLabels[convId] = labels },
  onStatusChanged: (convId, status) => {
    const idx = conversations.value.findIndex(c => c.id === convId)
    if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], status }
  },
  onAutoAssigned: (convId, agentId) => {
    const me = authUser.value
    if (!me) return
    convAssignee[convId] = { id: agentId, name: me.name, thumbnail: me.avatar_url }
    const idx = conversations.value.findIndex(c => c.id === convId)
    if (idx !== -1) conversations.value[idx] = {
      ...conversations.value[idx],
      assignee: { id: agentId, name: me.name, email: me.email, role: 'agent', thumbnail: me.avatar_url, availability: 'online' },
    }
  },
})

function onFilePick(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files) { files.value.push(...Array.from(input.files)); input.value = '' }
}

function growTa() {
  const el = taEl.value
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 120) + 'px'
}

function insertEmoji(em: string) {
  const el = taEl.value
  if (!el) { draft.value += em; return }
  const start = el.selectionStart ?? draft.value.length
  const end   = el.selectionEnd   ?? draft.value.length
  draft.value = draft.value.slice(0, start) + em + draft.value.slice(end)
  nextTick(() => {
    el.selectionStart = el.selectionEnd = start + em.length
    el.focus()
    growTa()
  })
}

function scrollBottom() {
  if (msgsEl.value) msgsEl.value.scrollTop = msgsEl.value.scrollHeight
}

/* ── Resize ──────────────────────────────── */
let _rx = false, _sx = 0, _sw = 0

function startResize(e: MouseEvent) {
  _rx = true; _sx = e.clientX; _sw = panelWidth.value
  document.addEventListener('mousemove', doResize)
  document.addEventListener('mouseup', stopResize)
  document.body.style.userSelect = 'none'
  document.body.style.cursor = 'col-resize'
}
function doResize(e: MouseEvent) {
  if (!_rx) return
  panelWidth.value = Math.max(MIN_W, Math.min(MAX_W, _sw + (_sx - e.clientX)))
}
function stopResize() {
  _rx = false
  document.removeEventListener('mousemove', doResize)
  document.removeEventListener('mouseup', stopResize)
  document.body.style.userSelect = ''
  document.body.style.cursor = ''
  localStorage.setItem('cdr_panel_w', String(panelWidth.value))
}

/* ── Polling ─────────────────────────────── */
let pollTimer: ReturnType<typeof setInterval> | null = null

watch(chatOpen, open => {
  if (open && selConvId.value) {
    pollTimer = setInterval(() => { if (selConvId.value) fetchMsgs(selConvId.value, true) }, 8000)
  } else {
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
  }
})

// Mensagens em tempo real (Fase 5 do plano — docs/planos/PLANO-REALTIME-ACTIONCABLE.md):
// aplica a mensagem do próprio evento direto no cache (onRealtimeMsg, sem
// refetch) na hora que chega message.created/updated pra conversa aberta, em
// vez de esperar o próximo tick do poll de 8s acima — que continua existindo
// do mesmo jeito, como rede de segurança (cobre a limitação de inbox/admin
// da Fase 3, e qualquer evento perdido).
let msgRealtimeUnsub: (() => void) | null = null

watch([chatOpen, selConvId], ([open, convId]) => {
  msgRealtimeUnsub?.()
  msgRealtimeUnsub = null
  if (open && convId) {
    msgRealtimeUnsub = onConversationMessage(convId, message => onRealtimeMsg(convId, message))
  }
})

let _nowTimer: ReturnType<typeof setInterval> | null = null

onMounted(() => {
  conversations.value.forEach(c => { prevUnreadMap[c.id] = c.unread_count })
  _nowTimer = setInterval(() => { cdrNowSec.value = Math.floor(Date.now() / 1000) }, 30_000)
})

onUnmounted(() => {
  document.removeEventListener('mousemove', doResize)
  document.removeEventListener('mouseup', stopResize)
  if (pollTimer) clearInterval(pollTimer)
  if (_nowTimer) clearInterval(_nowTimer)
  msgRealtimeUnsub?.()
  disposeAudioEls()
  cancelRecording()
  cancelVideoRecording()
  document.removeEventListener('click', onLblDocClick)
  document.removeEventListener('click', onCdrStatusDocClick)
  document.removeEventListener('click', onCdrTeamDocClick)
})

/* ── Helpers ─────────────────────────────── */
function previewText(convId: number): string {
  const msgs = msgCache.value[convId]
  if (!msgs?.length) return 'Abrir conversa…'
  const last = msgs[msgs.length - 1]
  if (last?.content) return last.content.slice(0, 60)
  if (last?.attachments?.length) {
    const t = last.attachments[0].file_type
    return t === 'image' ? '📷 Imagem' : t === 'audio' ? '🎵 Áudio' : t === 'video' ? '🎥 Vídeo' : '📎 Arquivo'
  }
  return '—'
}

function convTime(conv: Conversation): string {
  const ts = conv.created_at; if (!ts) return ''
  const d = new Date(ts * 1000), now = new Date()
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

function fmtTs(ts: number): string {
  const d = new Date(ts * 1000)
  return `${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`
}

function statusDot(conv: Conversation): string {
  if (conv.unread_count > 0 && !localReadConvIds.has(conv.id)) return 'dot-new'
  if (conv.status === 'pending') return 'dot-pending'
  if (conv.status === 'snoozed') return 'dot-snoozed'
  return 'dot-open'
}

function statusLabel(conv: Conversation): string {
  if (conv.unread_count > 0 && !localReadConvIds.has(conv.id)) return 'Nova mensagem'
  if (conv.status === 'pending') return 'Pendente'
  if (conv.status === 'snoozed') return 'Adiado'
  return 'Em aberto'
}

function convStatusLabel(s: string): string {
  return ({ open: 'Aberto', resolved: 'Resolvido', pending: 'Pendente', snoozed: 'Adiado' } as Record<string,string>)[s] ?? 'Aberto'
}

// Copiar telefone do contato (cabeçalho do chat) — feedback visual breve, mesmo
// padrão do copyJson em TemplatesView.vue (ref booleana + timeout de 2s).
const phoneCopied = ref(false)
async function copyPhone(phone: string) {
  if (!phone) return
  await navigator.clipboard.writeText(phone)
  phoneCopied.value = true
  setTimeout(() => { phoneCopied.value = false }, 2000)
}

function onCdrStatusDocClick() {
  cdrStatusMenuOpen.value = false
  cdrSnoozeMode.value = false
}
watch(cdrStatusMenuOpen, open => {
  if (open) setTimeout(() => document.addEventListener('click', onCdrStatusDocClick), 0)
  else document.removeEventListener('click', onCdrStatusDocClick)
})

async function cdrChangeStatus(status: string, snoozedUntil: number | null = null) {
  if (cdrStatusLoading.value || !activeConv.value) return
  if (status === 'resolved') {
    cdrStatusMenuOpen.value = false
    if (!(await confirmResolveConversation())) return
  }
  cdrStatusLoading.value = true
  cdrStatusMenuOpen.value = false
  cdrSnoozeMode.value = false
  const convId = activeConv.value.id
  try {
    let newStatus: string | null
    if (status === 'snoozed') {
      const result = await snoozeConversation(convId, authUser.value?.access_token || '', snoozedUntil)
      newStatus = result.status
    } else if (status === 'resolved') {
      const conv = activeConv.value
      const context: MacroActionContext = {
        contactPhone:       conv?.contact?.phone ?? null,
        contactName:        conv?.contact?.name  ?? null,
        contactEmail:       (conv?.contact as any)?.email ?? null,
        teamId:             conv?.team?.id   ?? null,
        teamName:           conv?.team?.name ?? null,
        conversationStatus: conv?.status ?? null,
        contactId:          conv?.contact?.id ?? null,
        assigneeId:         conv?.assignee?.id ?? null,
        assigneeName:       conv?.assignee?.name ?? null,
        labels:             conv?.labels ?? [],
        accountId:          authUser.value?.account_id ?? null,
        executedById:       authUser.value?.id ?? null,
        executedByName:     authUser.value?.name ?? null,
      }
      const outcome = await resolveConversationSmart(convId, authUser.value?.access_token || '', context)
      if (!outcome.ok) return
      newStatus = outcome.status ?? 'resolved'
    } else {
      newStatus = await toggleConversationStatus(convId, authUser.value?.access_token || '', status)
      if (newStatus === null) return
    }
    const idx = conversations.value.findIndex(c => c.id === convId)
    if (idx !== -1) conversations.value[idx] = { ...conversations.value[idx], status: newStatus, snoozed_until: snoozedUntil }
    // myConvs exclui resolvidas — sem isso activeConv fica null com chatOpen
    // ainda true, e nem a lista nem o chat renderizam (painel em branco).
    if (newStatus === 'resolved') backToList()
  } catch { /* silencioso */ } finally {
    cdrStatusLoading.value = false
  }
}

function cdrConfirmSnooze() {
  const total = (cdrSnoozeH.value || 0) * 3600 + (cdrSnoozeM.value || 0) * 60
  const until = total > 0 ? Math.floor(Date.now() / 1000) + total : null
  cdrChangeStatus('snoozed', until)
}

function senderName(msg: ChatMessage): string {
  return msg.sender?.name || msg.sender?.available_name || '?'
}

function chatwootUrl(id: number): string {
  return `https://chatwoot.isbet.org.br/app/accounts/3/conversations/${id}`
}
</script>

<style scoped>
/* ════════════════════════════════════════════
   Strip
════════════════════════════════════════════ */
.cdr-strip {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 60px;
  background: #021627;
  border-left: 1px solid rgba(255,255,255,.06);
  box-shadow: -3px 0 18px rgba(2,22,39,.45);
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 190;
  overflow: visible;
}

/* Cap — preenche o topo alinhado ao cabeçalho do sidebar */
.cdr-strip-cap {
  width: 100%;
  height: 66px;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

/* Cabeçalho */
.cdr-strip-head {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  cursor: pointer;
  color: rgba(255,255,255,.45);
  border-bottom: 1px solid rgba(255,255,255,.07);
  flex-shrink: 0;
  transition: background 0.14s, color 0.14s;
}
.cdr-strip-head:hover { background: rgba(255,255,255,.07); color: rgba(255,255,255,.9); }
.is-expanded .cdr-strip-head { color: #fff; }

.cdr-strip-badge {
  position: absolute;
  top: 7px; right: 7px;
  background: #BC0C24;
  color: #fff;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 800;
  min-width: 15px; height: 15px;
  line-height: 15px;
  text-align: center;
  padding: 0 3px;
}

.cdr-av-list-wrap {
  position: relative;
  flex: 1;
  min-height: 0;
  width: 100%;
  display: flex;
  flex-direction: column;
}

/* Lista de avatares — scrollável */
.cdr-av-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 8px 0 12px;
  scrollbar-width: none;
}

/* Setas de rolagem — só aparecem quando há mais conversas do que cabem */
.cdr-scroll-btn {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: 30px; height: 20px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(255,255,255,.9);
  border: none;
  border-radius: 999px;
  color: #021627;
  cursor: pointer;
  z-index: 3;
  box-shadow: 0 2px 8px rgba(0,0,0,.45);
  transition: background 0.14s, transform 0.14s;
}
.cdr-scroll-btn:hover { background: #BC0C24; color: #fff; transform: translateX(-50%) scale(1.1); }
.cdr-scroll-btn.up   { top: 4px; }
.cdr-scroll-btn.down { bottom: 4px; }

/* Rodapé — alterna entre chat de clientes e chat entre agentes (useChatRailMode) */
.cdr-strip-footer {
  position: relative;
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(255,255,255,.45);
  border-top: 1px solid rgba(255,255,255,.07);
  flex-shrink: 0;
  transition: background 0.14s, color 0.14s;
}
.cdr-strip-footer:hover { background: rgba(255,255,255,.07); color: rgba(255,255,255,.9); }
.cdr-strip-footer-badge {
  position: absolute;
  top: 5px; right: 9px;
  background: #BC0C24;
  color: #fff;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 800;
  min-width: 15px; height: 15px;
  display: flex; align-items: center; justify-content: center;
  padding: 0 3px;
  line-height: 1;
}
.cdr-av-list::-webkit-scrollbar { display: none; }

.cdr-strip-empty {
  padding: 16px 0;
  color: rgba(255,255,255,.18);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Avatar item */
/* Avatar item — círculo simples */
.cdr-av-item {
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.15s cubic-bezier(0.34, 1.56, 0.64, 1);
  flex-shrink: 0;
}
.cdr-av-item:hover   { transform: scale(1.1); }
.cdr-av-item.active::before {
  content: '';
  position: absolute;
  inset: -3px;
  border-radius: 50%;
  border: 2.5px solid #BC0C24;
}

/* ── Card de hover — retângulo com bordas arredondadas (não pílula), mesmo
   gatilho/posicionamento de antes, mesmo padrão visual do ach-tt-card
   (AgentChatDrawer.vue) ── */
.cdr-tt-card {
  position: fixed;
  transform: translateY(-50%);
  width: 230px;
  background: rgba(2, 22, 39, 0.97);
  border: 1px solid rgba(255,255,255,0.13);
  border-radius: 14px;
  padding: 12px 14px;
  pointer-events: none;
  z-index: 9500;
  box-shadow: 0 8px 32px rgba(0,0,0,.6);
  backdrop-filter: blur(8px);
}

.tt-pill-enter-active {
  transition: opacity 0.18s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.tt-pill-leave-active { transition: opacity 0.12s ease; }
.tt-pill-enter-from   { opacity: 0; transform: translateY(-50%) translateX(16px); }
.tt-pill-leave-to     { opacity: 0; }

.cdr-ttc-head { display: flex; align-items: center; gap: 10px; }
.cdr-ttc-av {
  position: relative;
  width: 38px; height: 38px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800;
  color: #fff;
  overflow: hidden;
  border: 2px solid rgba(255,255,255,0.15);
}
.cdr-ttc-av img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.cdr-ttc-av-dot {
  position: absolute; bottom: -1px; right: -1px;
  width: 9px; height: 9px; border-radius: 50%;
  border: 2px solid #021627;
}
.cdr-ttc-av-dot.dot-new     { background: #22c55e; box-shadow: 0 0 5px rgba(34,197,94,.6); }
.cdr-ttc-av-dot.dot-pending { background: #f97316; }
.cdr-ttc-av-dot.dot-open    { background: #3b82f6; }
.cdr-ttc-av-dot.dot-snoozed { background: #f59e0b; }

.cdr-ttc-headinfo { min-width: 0; flex: 1; }
.cdr-ttc-row1 { display: flex; align-items: center; gap: 8px; }
.cdr-ttc-name {
  font-size: 13px; font-weight: 700; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  flex: 1; min-width: 0;
}
.cdr-ttc-unread {
  background: #BC0C24; color: #fff;
  font-size: 10px; font-weight: 800;
  padding: 1px 7px; border-radius: 999px;
  white-space: nowrap; flex-shrink: 0; line-height: 1.5;
}
.cdr-ttc-id {
  display: block; font-size: 10.5px; color: rgba(255,255,255,0.4);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin-top: 2px;
}

.cdr-ttc-divider { height: 1px; background: rgba(255,255,255,0.09); margin: 10px 0; }

.cdr-ttc-body { display: flex; flex-direction: column; gap: 6px; }
.cdr-ttc-row {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; color: rgba(255,255,255,0.65);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cdr-ttc-row svg { flex-shrink: 0; }
.cdr-ttc-row--phone {
  color: rgba(255,255,255,0.85);
  font-weight: 600;
  letter-spacing: .1px;
}
.cdr-ttc-status {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 600; color: rgba(255,255,255,.75);
}
.cdr-ttc-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.cdr-ttc-dot.dot-new     { background: #22c55e; box-shadow: 0 0 5px rgba(34,197,94,.6); }
.cdr-ttc-dot.dot-pending { background: #f97316; }
.cdr-ttc-dot.dot-open    { background: #3b82f6; }
.cdr-ttc-dot.dot-snoozed { background: #f59e0b; }

/* Badge de não lidas no avatar da strip */
.cdr-av-unread-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  min-width: 17px;
  height: 17px;
  background: var(--red);
  color: #fff;
  border-radius: 9px;
  font-size: 9px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  border: 2px solid var(--night);
  line-height: 1;
  z-index: 2;
  animation: cdr-pop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  pointer-events: none;
}
@keyframes cdr-pop { from { transform: scale(0); } to { transform: scale(1); } }

/* Avatar */
.cdr-av {
  width: 38px; height: 38px;
  border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800;
  color: #fff;
  letter-spacing: .4px;
  user-select: none;
  flex-shrink: 0;
  overflow: hidden;
}
.cdr-av img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; display: block; }
.cdr-av.sm2 { width: 30px; height: 30px; font-size: 11px; }

/* Status dot */
.cdr-status-dot {
  position: absolute;
  bottom: 1px; right: 1px;
  width: 12px; height: 12px;
  border-radius: 50%;
  border: 2px solid #021627;
}
.cdr-status-dot.sm { width: 10px; height: 10px; }

.cdr-status-dot.dot-new     { background: #22c55e; animation: cdr-pulse 1.8s ease-in-out infinite; }
.cdr-status-dot.dot-pending { background: #f97316; }
.cdr-status-dot.dot-open    { background: #3b82f6; }
.cdr-status-dot.dot-snoozed { background: #f59e0b; }

@keyframes cdr-pulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,.55); }
  50%      { box-shadow: 0 0 0 4px rgba(34,197,94,0); }
}

/* ════════════════════════════════════════════
   Tooltip fixo (via Teleport — sem clipping)
════════════════════════════════════════════ */
.cdr-tt {
  position: fixed;
  transform: translateY(-50%);
  background: #18181b;
  color: #f4f4f5;
  border-radius: 13px;
  padding: 14px;
  min-width: 220px;
  max-width: 280px;
  box-shadow: 0 12px 40px rgba(0,0,0,.45);
  z-index: 9000;
  pointer-events: none;
}
.cdr-tt::after {
  content: '';
  position: absolute;
  right: -6px; top: 50%;
  transform: translateY(-50%);
  border: 7px solid transparent;
  border-right: none;
  border-left-color: #18181b;
}

.tt-fade-enter-active, .tt-fade-leave-active { transition: opacity 0.15s, transform 0.15s; }
.tt-fade-enter-from, .tt-fade-leave-to { opacity: 0; transform: translateY(-50%) translateX(6px); }

/* Cabeçalho */
.cdr-tt-head {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 2px;
}
.cdr-tt-av {
  width: 38px; height: 38px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 13px; font-weight: 800; color: #fff;
  flex-shrink: 0; overflow: hidden;
}
.cdr-tt-av img { width: 100%; height: 100%; object-fit: cover; }
.cdr-tt-head-info { flex: 1; min-width: 0; }
.cdr-tt-name {
  font-size: 13px; font-weight: 700;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cdr-tt-company {
  font-size: 10.5px; color: #a1a1aa; margin-top: 1px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* Divisor */
.cdr-tt-divider {
  border: none; border-top: 1px solid rgba(255,255,255,.08);
  margin: 9px 0;
}

/* Linhas de info */
.cdr-tt-row {
  display: flex; align-items: center; gap: 6px;
  font-size: 11px; color: #a1a1aa;
  margin-bottom: 4px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.cdr-tt-row svg { flex-shrink: 0; color: #71717a; }

/* Bio */
.cdr-tt-bio {
  font-size: 10.5px; color: #71717a; font-style: italic;
  margin: 6px 0; line-height: 1.4;
  display: -webkit-box; -webkit-line-clamp: 2;
  -webkit-box-orient: vertical; overflow: hidden;
}

/* Redes sociais */
.cdr-tt-socials {
  display: flex; gap: 4px; flex-wrap: wrap;
  margin: 6px 0;
}
.cdr-tt-social {
  display: inline-flex; align-items: center; justify-content: center;
  width: 26px; height: 20px; border-radius: 5px;
  font-size: 9px; font-weight: 800; color: #fff;
  text-decoration: none; letter-spacing: .3px;
  pointer-events: auto;
  transition: opacity .13s;
}
.cdr-tt-social:hover { opacity: .82; }

/* Etiquetas */
.cdr-tt-lbls {
  display: flex; flex-wrap: wrap; gap: 3px;
  margin: 6px 0;
}
.cdr-tt-lbl {
  background: rgba(59,130,246,.22);
  color: #93c5fd;
  border-radius: 4px; font-size: 10px; font-weight: 600;
  padding: 1px 6px; text-transform: capitalize;
}

/* Rodapé */
.cdr-tt-footer {
  display: flex; align-items: center; gap: 5px;
  font-size: 11px; color: #d1d5db;
  border-top: 1px solid rgba(255,255,255,.08);
  padding-top: 8px; margin-top: 6px;
}
.cdr-tt-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.cdr-tt-dot.dot-new     { background: #22c55e; }
.cdr-tt-dot.dot-pending { background: #f97316; }
.cdr-tt-dot.dot-open    { background: #3b82f6; }
.cdr-tt-unread {
  margin-left: auto;
  background: #BC0C24; color: #fff;
  border-radius: 999px; font-size: 9.5px; font-weight: 700;
  padding: 1px 6px;
}

/* ════════════════════════════════════════════
   Painel
════════════════════════════════════════════ */
.cdr-panel {
  position: fixed;
  top: 66px; bottom: 0;
  background: #fff;
  box-shadow: -6px 0 28px rgba(0,0,0,.12);
  display: flex;
  flex-direction: column;
  z-index: 189;
  overflow: hidden;
}

.cdr-panel-enter-active, .cdr-panel-leave-active {
  transition: opacity .22s ease, transform .22s cubic-bezier(.4,0,.2,1);
}
.cdr-panel-enter-from, .cdr-panel-leave-to { opacity: 0; transform: translateX(20px); }

/* Handle de resize */
.cdr-resize-handle {
  position: absolute; left: 0; top: 0; bottom: 0; width: 5px;
  cursor: col-resize; z-index: 10;
  transition: background 0.15s;
}
.cdr-resize-handle:hover { background: rgba(188,12,36,.18); }

/* Cabeçalho do painel */
.cdr-head {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 12px; min-height: 56px;
  background: #1a0407;
  color: #fff;
  flex-shrink: 0;
  border-bottom: 1px solid rgba(0,0,0,.25);
}
.cdr-head-title {
  flex: 1; font-size: 12.5px; font-weight: 700;
  display: flex; align-items: center; gap: 7px;
  letter-spacing: .2px; min-width: 0;
}
.cdr-head-badge {
  background: rgba(255,255,255,.22);
  border-radius: 999px; font-size: 11px; font-weight: 700;
  padding: 1px 7px;
}

/* Switch auto-abrir */
.cdr-auto-wrap {
  display: flex; align-items: center; gap: 5px;
  padding: 4px 7px; border-radius: 20px;
  border: 1.5px solid rgba(255,255,255,.2);
  color: rgba(255,255,255,.55);
  cursor: default;
  transition: all .15s;
  flex-shrink: 0;
}
.cdr-auto-wrap--on {
  border-color: rgba(255,255,255,.6);
  color: #fff;
  background: rgba(255,255,255,.1);
}
.cdr-auto-sw {
  position: relative; width: 30px; height: 16px; flex-shrink: 0; cursor: pointer;
}
.cdr-auto-sw input { opacity: 0; width: 0; height: 0; }
.cdr-auto-track {
  position: absolute; inset: 0;
  background: rgba(255,255,255,.25);
  border-radius: 16px; cursor: pointer;
  transition: background .15s;
}
.cdr-auto-track::before {
  content: ''; position: absolute;
  width: 10px; height: 10px;
  left: 3px; top: 3px;
  background: #fff; border-radius: 50%;
  transition: transform .15s;
  box-shadow: 0 1px 2px rgba(0,0,0,.2);
}
.cdr-auto-sw input:checked + .cdr-auto-track { background: #22c55e; }
.cdr-auto-sw input:checked + .cdr-auto-track::before { transform: translateX(14px); }

.cdr-icon-btn {
  width: 28px; height: 28px; border-radius: 7px;
  background: rgba(255,255,255,.15); border: none;
  display: flex; align-items: center; justify-content: center;
  color: #fff; cursor: pointer; flex-shrink: 0;
  transition: background 0.13s;
}
.cdr-icon-btn:hover { background: rgba(255,255,255,.3); }

.cdr-chat-hinfo { flex: 1; min-width: 0; }
.cdr-chat-hname-row { display: flex; align-items: center; gap: 7px; min-width: 0; }
.cdr-chat-hname { font-size: 12.5px; font-weight: 700; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex-shrink: 1; min-width: 0; }
.cdr-chat-hdoc  { font-size: 10px; color: rgba(255,255,255,.55); margin-top: 1px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cdr-chat-hsub  { font-size: 10px; color: rgba(255,255,255,.68); margin-top: 2px; display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }

.cdr-chat-hphone {
  display: flex; align-items: center; gap: 5px;
  flex-shrink: 0;
  background: rgba(255,255,255,.1);
  border: 1px solid rgba(255,255,255,.14);
  border-radius: 20px;
  padding: 2px 4px 2px 8px;
  font-size: 10.5px; font-weight: 600;
  color: rgba(255,255,255,.85);
  letter-spacing: .1px;
  white-space: nowrap;
}
.cdr-chat-hphone svg { flex-shrink: 0; opacity: .75; }
.cdr-phone-copy-btn {
  display: flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; flex-shrink: 0;
  border: none; border-radius: 50%;
  background: rgba(255,255,255,.14);
  color: rgba(255,255,255,.85);
  cursor: pointer;
  transition: background .14s, color .14s;
}
.cdr-phone-copy-btn:hover { background: rgba(255,255,255,.28); color: #fff; }
.cdr-phone-copy-btn.is-copied { background: #22c55e; color: #fff; }
.cdr-chat-assignee {
  display: flex; align-items: center; gap: 4px; margin-top: 2px;
  font-size: 9.5px; color: rgba(255,255,255,.55); font-weight: 500;
}
.cdr-chat-assignee svg { flex-shrink: 0; opacity: .7; }
.cdr-chat-assignee--none { opacity: .45; font-style: italic; }
.cdr-fluxo-tag {
  display: inline-flex; align-items: center;
  margin-left: 5px; vertical-align: middle;
  padding: 1px 6px; border-radius: 4px;
  font-size: 9px; font-weight: 700; letter-spacing: .4px; text-transform: uppercase;
  background: rgba(255,255,255,.18);
  color: rgba(255,255,255,.95);
  border: 1px solid rgba(255,255,255,.28);
  white-space: nowrap;
}
.cdr-item-assignee {
  display: flex; align-items: center; gap: 3px; margin-top: 3px;
  font-size: 10px; color: #6b7280; font-weight: 500; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
}
.cdr-item-assignee svg { flex-shrink: 0; color: #9ca3af; }
.cdr-item-assignee--none { color: #d1d5db; font-style: italic; }

.cdr-item-fluxo {
  display: inline-block; margin-top: 4px;
  padding: 1px 7px; border-radius: 5px;
  font-size: 9px; font-weight: 700; letter-spacing: .3px; text-transform: uppercase;
  background: rgba(124,58,237,.10); color: #7c3aed;
  border: 1px solid rgba(124,58,237,.20);
  white-space: nowrap;
}

/* Busca/filtro */
.cdr-search {
  display: flex; align-items: center; gap: 8px;
  flex-shrink: 0;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(0,0,0,.06);
  color: #94a3b8;
}
.cdr-search-input {
  flex: 1; min-width: 0;
  border: none; outline: none;
  background: transparent;
  font-size: 12.5px;
  font-family: inherit;
  color: #1a1a1a;
}
.cdr-search-input::placeholder { color: #94a3b8; }
.cdr-search-clear {
  display: flex; align-items: center; justify-content: center;
  width: 18px; height: 18px;
  border: none; border-radius: 50%;
  background: rgba(0,0,0,.06);
  color: #64748b;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.14s;
}
.cdr-search-clear:hover { background: rgba(0,0,0,.12); }

/* Empty */
.cdr-empty {
  flex: 1; display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  gap: 14px; color: #9ca3af; padding: 40px 24px;
}
.cdr-empty p { font-size: 12.5px; text-align: center; line-height: 1.5; }

/* Lista */
.cdr-list { flex: 1; overflow-y: auto; overscroll-behavior: contain; }

.cdr-item {
  display: flex; align-items: flex-start; gap: 10px;
  padding: 11px 14px; cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.12s;
  position: relative;
}
.cdr-item:hover    { background: #fafafa; }
.cdr-item.selected { background: rgba(188,12,36,.04); border-left: 3px solid #BC0C24; padding-left: 11px; }
.cdr-item:last-child { border-bottom: none; }

/* Botão janela flutuante */
.cdr-float-btn {
  position: absolute; top: 8px; right: 10px;
  width: 22px; height: 22px; border-radius: 5px; border: none;
  background: rgba(188,12,36,.09); color: #BC0C24;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; opacity: 0; flex-shrink: 0;
  transition: opacity .15s, background .15s;
}
.cdr-item:hover .cdr-float-btn { opacity: 1; }
.cdr-float-btn:hover { background: rgba(188,12,36,.18); }

.cdr-item-av-wrap { position: relative; flex-shrink: 0; }
.cdr-item-av {
  width: 36px; height: 36px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 12px; font-weight: 800; color: #fff; letter-spacing: .3px;
}

.cdr-item-body { flex: 1; min-width: 0; }
.cdr-item-r1, .cdr-item-r2 { display: flex; align-items: center; justify-content: space-between; gap: 6px; }
.cdr-item-r2 { margin-top: 2px; }

.cdr-item-name { font-size: 12.5px; font-weight: 700; color: #1a1a1a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; min-width: 0; }
.cdr-item-time { font-size: 10.5px; color: #9ca3af; white-space: nowrap; flex-shrink: 0; }

.cdr-item-prio {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 1px 6px; border-radius: 10px; flex-shrink: 0;
  font-size: 9px; font-weight: 800; letter-spacing: .3px; text-transform: uppercase;
  border: 1px solid transparent;
}
.cdr-item-prio-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
.cdr-item-preview { font-size: 11.5px; color: #6b7280; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; flex: 1; }

.cdr-unread {
  background: #BC0C24; color: #fff; border-radius: 999px;
  font-size: 10px; font-weight: 700;
  min-width: 18px; height: 18px; line-height: 18px;
  text-align: center; padding: 0 4px; flex-shrink: 0;
}
.cdr-item-lbls { display: flex; gap: 3px; margin-top: 5px; flex-wrap: wrap; }
.cdr-lbl {
  background: rgba(59,130,246,.1); color: #2563eb;
  border-radius: 4px; font-size: 10px; font-weight: 600;
  padding: 1px 5px; text-transform: capitalize; white-space: nowrap;
}

/* ════════════════════════════════════════════
   Etiquetas interativas
════════════════════════════════════════════ */
.cdr-chat-lbls {
  display: flex; gap: 4px; flex-wrap: wrap; align-items: center;
  padding: 6px 12px; background: #fafafa;
  border-bottom: 1px solid #f3f4f6; flex-shrink: 0;
  position: relative;
}

.cdr-lbl-active {
  display: inline-flex; align-items: center; gap: 2px;
  padding-right: 4px;
}

.cdr-lbl-x {
  background: none; border: none; cursor: pointer;
  font-size: 14px; line-height: 1; padding: 0 1px;
  color: inherit; opacity: .55;
  transition: opacity .13s, color .13s;
}
.cdr-lbl-x:hover:not(:disabled) { opacity: 1; color: #BC0C24; }
.cdr-lbl-x:disabled { cursor: default; opacity: .25; }

.cdr-lbl-lock { flex-shrink: 0; opacity: .6; }

/* Botão "+ etiqueta" */
.cdr-lbl-picker { position: relative; }

.cdr-lbl-add-btn {
  display: inline-flex; align-items: center; gap: 3px;
  background: none; border: 1.5px dashed #d1d5db;
  border-radius: 4px; font-size: 10px; font-weight: 600;
  color: #9ca3af; cursor: pointer; padding: 2px 7px;
  transition: border-color .13s, color .13s, background .13s;
}
.cdr-lbl-add-btn:hover:not(:disabled) { border-color: #BC0C24; color: #BC0C24; background: rgba(188,12,36,.04); }
.cdr-lbl-add-btn:disabled { opacity: .45; cursor: default; }

/* Dropdown */
.cdr-lbl-dropdown {
  position: absolute; left: 0; top: calc(100% + 5px);
  background: #fff; border: 1px solid #e5e7eb;
  border-radius: 10px; box-shadow: 0 8px 28px rgba(0,0,0,.13);
  z-index: 600; min-width: 200px; max-height: 240px;
  overflow-y: auto; padding: 4px;
}

.cdr-lbl-loading {
  display: flex; align-items: center; justify-content: center;
  padding: 14px;
}

.cdr-lbl-opt {
  display: flex; align-items: center; gap: 8px;
  padding: 7px 9px; border-radius: 7px;
  cursor: pointer; font-size: 11.5px; color: #374151;
  transition: background .12s; user-select: none;
}
.cdr-lbl-opt:hover { background: #f3f4f6; }
.cdr-lbl-opt.checked { background: rgba(37,99,235,.06); }

.cdr-lbl-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
.cdr-lbl-opt-name { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ── Prioridade ── */
/* ── Agente selector ── */
.cdr-agent-wrap { position: relative; flex-shrink: 0; }
.cdr-agent-sel {
  display: inline-flex; align-items: center; gap: 4px;
  border: 1px solid var(--ink-10); border-radius: 999px;
  padding: 2px 8px; font-size: 11px; font-weight: 600;
  color: var(--ink-60); cursor: pointer; background: var(--ink-02);
  transition: border-color .15s, color .15s;
}
.cdr-agent-sel:hover { border-color: #BC0C24; color: #BC0C24; }
.cdr-agent-menu {
  position: absolute; top: calc(100% + 5px); left: 0; z-index: 25;
  width: 220px; background: var(--white); border: 1px solid var(--ink-10);
  border-radius: 10px; box-shadow: 0 6px 24px rgba(0,0,0,.18); overflow: hidden;
}
.cdr-agent-search-wrap { padding: 7px 8px; border-bottom: 1px solid var(--ink-05); }
.cdr-agent-search {
  width: 100%; border: 1px solid var(--ink-10); border-radius: 7px;
  padding: 5px 8px; font-size: 12px; outline: none; box-sizing: border-box;
  background: var(--white); color: var(--ink-80); font-family: inherit;
}
.cdr-agent-search:focus { border-color: #BC0C24; }
.cdr-agent-list { max-height: 210px; overflow-y: auto; }
.cdr-agent-opt {
  display: flex; align-items: center; gap: 8px; width: 100%;
  border: none; background: none; padding: 8px 10px;
  font-size: 12.5px; cursor: pointer; transition: background .12s;
  font-family: inherit; color: var(--ink-70); text-align: left;
  white-space: nowrap; overflow: hidden;
}
.cdr-agent-opt:hover { background: var(--ink-02); }
.cdr-agent-opt.active { background: rgba(34,197,94,.05); }
.cdr-agent-av {
  width: 22px; height: 22px; border-radius: 50%; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  font-size: 9px; font-weight: 700; color: #fff;
}
.cdr-agent-av-wrap { position: relative; flex-shrink: 0; display: inline-flex; }
.cdr-agent-status-dot {
  position: absolute; bottom: -1px; right: -1px;
  width: 8px; height: 8px; border-radius: 50%;
  border: 1.5px solid var(--white);
  box-shadow: 0 1px 3px rgba(0,0,0,.18);
}
.casd-online  { background: var(--green); }
.casd-busy    { background: var(--orange); }
.casd-offline { background: var(--ink-40); }

.cdr-priority-wrap { position: relative; flex-shrink: 0; }
.cdr-priority-tag {
  display: inline-flex; align-items: center; gap: 4px;
  border: 1px solid; border-radius: 999px;
  padding: 2px 8px; font-size: 11px; font-weight: 700;
  cursor: pointer; transition: opacity .15s; user-select: none;
}
.cdr-priority-tag:hover { opacity: .75; }
.cdr-priority-menu {
  position: absolute; left: 0; top: calc(100% + 5px); z-index: 50;
  min-width: 150px; background: #fff; border: 1px solid #e5e7eb;
  border-radius: 10px; box-shadow: 0 6px 24px rgba(0,0,0,.13);
  overflow: hidden;
}
.cdr-priority-opt {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px; width: 100%; border: none; background: none;
  font-size: 13px; cursor: pointer; transition: background .12s;
  font-family: inherit; color: #374151; text-align: left;
}
.cdr-priority-opt:hover { background: #f3f4f6; }
.cdr-priority-opt.active { background: rgba(34,197,94,.06); }
.cdr-priority-dot { width: 9px; height: 9px; border-radius: 50%; flex-shrink: 0; }
.cdr-p-spin {
  display: inline-block; width: 9px; height: 9px;
  border: 1.5px solid rgba(0,0,0,.12); border-top-color: currentColor;
  border-radius: 50%; animation: cdr-spin .7s linear infinite;
}

/* Mensagens */
.cdr-msgs {
  flex: 1; overflow-y: auto;
  padding: 10px 10px 6px;
  display: flex; flex-direction: column; gap: 2px;
  background: #f7f7f7; overscroll-behavior: contain;
}
.cdr-msgs-loading { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; }
.cdr-msgs-loading-label { font-size: 12px; color: #888; }
.cdr-load-more-row { display: flex; justify-content: center; padding: 10px 0; }
.cdr-spinner-sm { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(0,0,0,.12); border-top-color: #888; border-radius: 50%; animation: cdr-spin .7s linear infinite; }
.cdr-no-more { text-align: center; font-size: 11px; color: #bbb; padding: 8px 0; }

/* Esqueleto de bolhas — carregamento inicial de mensagens (Fase 3, PLANO-OTIMIZACAO-CARREGAMENTO-MENSAGENS.md) */
.cdr-msg-skeleton { width: 100%; display: flex; flex-direction: column; gap: 10px; padding: 0 2px; }
.cdr-sk-row { display: flex; }
.cdr-sk-row.inn { justify-content: flex-start; }
.cdr-sk-row.out { justify-content: flex-end; }
.cdr-sk-bubble {
  height: 30px; border-radius: 14px;
  background: linear-gradient(90deg, #e9e9e9 25%, #f4f4f4 50%, #e9e9e9 75%);
  background-size: 200% 100%;
  animation: cdr-sk-shimmer 1.3s ease-in-out infinite;
}
@keyframes cdr-sk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

/* Linha de mensagem */
.cdr-msg-row {
  display: flex;
  align-items: flex-end;
  gap: 6px;
}
.cdr-msg-row.out { flex-direction: row-reverse; }
.cdr-msg-row.act { justify-content: center; }
.cdr-msg-row.inn { flex-direction: row; }

/* Avatar do remetente */
.cdr-msg-av {
  width: 26px; height: 26px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  font-size: 9.5px; font-weight: 800; color: #fff;
  flex-shrink: 0; overflow: hidden;
  align-self: flex-end;
}
.cdr-msg-av img { width: 100%; height: 100%; object-fit: cover; border-radius: 50%; }
.cdr-msg-av-spacer { width: 26px; flex-shrink: 0; }

.cdr-bubble {
  max-width: 74%; padding: 7px 10px 4px;
  border-radius: 14px; word-break: break-word;
}
.cdr-msg-row.out .cdr-bubble { background: #BC0C24; color: #fff; border-bottom-right-radius: 3px; }
.cdr-msg-row.inn .cdr-bubble { background: #fff; color: #1a1a1a; border-bottom-left-radius: 3px; box-shadow: 0 1px 3px rgba(0,0,0,.07); }
.cdr-bubble-text { display: block; font-size: 12.5px; line-height: 1.45; white-space: pre-wrap; }
.out .cdr-bubble-text .msg-sig {
  display: block;
  margin-top: 7px;
  padding-top: 6px;
  border-top: 1px solid rgba(255,255,255,0.28);
  font-size: 10.5px;
  font-style: italic;
  opacity: 0.7;
  letter-spacing: 0.01em;
}
.cdr-bubble-ts   { display: flex; justify-content: flex-end; align-items: center; gap: 2px; font-size: 10px; margin-top: 2px; }
.inn .cdr-bubble-ts { color: rgba(0,0,0,.45); }
.out .cdr-bubble-ts { color: rgba(255,255,255,.65); }
.cdr-msg-status  { display: inline-flex; align-items: center; flex-shrink: 0; }
.cdr-file-dl-btn {
  display: inline-flex; align-items: center; gap: 5px;
  margin-top: 5px; padding: 4px 9px; border-radius: 6px;
  background: rgba(255,255,255,.18); color: #fff;
  font-size: 11px; font-weight: 600; text-decoration: none;
  transition: background .15s;
}
.cdr-file-dl-btn:hover { background: rgba(255,255,255,.30); }
.out .cdr-msg-status.s-read   { color: #93C5FD; }
.out .cdr-msg-status.s-failed { color: #FECACA; }
.inn .cdr-msg-status.s-read   { color: #3B82F6; }
.inn .cdr-msg-status.s-failed { color: #EF4444; }
.cdr-msg-act { font-size: 10.5px; color: #9ca3af; background: #e5e7eb; border-radius: 999px; padding: 2px 11px; }

/* Nota interna */
.cdr-bubble--priv { background: #FEF3C7 !important; color: #78350F !important; box-shadow: 0 1px 3px rgba(0,0,0,.07); }
.cdr-msg-row.out .cdr-bubble--priv { border-bottom-right-radius: 3px; }
.cdr-msg-row.inn .cdr-bubble--priv { border-bottom-left-radius: 3px; }
.cdr-priv-tag { display: flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em; color: #92400E; margin-bottom: 3px; }
.cdr-bubble--priv .cdr-bubble-ts { color: rgba(120,53,15,.6); }

/* Anexos */
.cdr-files { display: flex; flex-wrap: wrap; gap: 4px; padding: 5px 11px; background: #fafafa; border-top: 1px solid #f3f4f6; flex-shrink: 0; }
.cdr-file-pill { display: inline-flex; align-items: center; gap: 4px; background: #f3f4f6; border-radius: 6px; padding: 2px 7px; font-size: 11px; color: #374151; max-width: 180px; }
.cdr-file-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.cdr-file-rm { background: none; border: none; cursor: pointer; font-size: 13px; color: #9ca3af; padding: 0; line-height: 1; }
.cdr-file-rm:hover { color: #BC0C24; }

/* Compositor */
.cdr-composer { display: flex; align-items: flex-end; gap: 7px; padding: 9px 11px; background: #fff; border-top: 1px solid #f3f4f6; flex-shrink: 0; transition: background .15s, border-color .15s; }
.cdr-composer--priv { background: #FFFBEB; border-top-color: #FDE68A; }
.cdr-attach-btn { width: 32px; height: 32px; border-radius: 8px; background: #f3f4f6; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #6b7280; transition: background .13s, color .13s; flex-shrink: 0; }
.cdr-attach-btn:hover { background: #e5e7eb; color: #BC0C24; }
.cdr-attach-btn.cdr-attach-disabled { opacity: .4; cursor: not-allowed; }
.cdr-attach-btn.cdr-attach-disabled:hover { background: #f3f4f6; color: #6b7280; }
.cdr-priv-btn { width: 32px; height: 32px; border: none; border-radius: 8px; background: #f3f4f6; display: flex; align-items: center; justify-content: center; cursor: pointer; color: #6b7280; transition: background .13s, color .13s; flex-shrink: 0; }
.cdr-priv-btn:hover { background: #e5e7eb; color: #92400E; }
.cdr-priv-btn:disabled { opacity: .4; cursor: not-allowed; }
.cdr-priv-btn--active, .cdr-priv-btn--active:hover { background: #F59E0B; color: #fff; }
.cdr-composer--priv .cdr-ta { background: #FFFBEB; border-color: #FDE68A; }
.cdr-composer--priv .cdr-ta:focus { border-color: #F59E0B; }
.cdr-ta { flex: 1; border: 1.5px solid #e5e7eb; border-radius: 10px; padding: 6px 10px; font-size: 12.5px; font-family: inherit; resize: none; outline: none; color: #1a1a1a; background: #fafafa; line-height: 1.4; transition: border-color .15s; max-height: 120px; overflow-y: auto; }
.cdr-ta:focus { border-color: #BC0C24; background: #fff; }
.cdr-ta::placeholder { color: #c0c0c0; }
.cdr-ta:disabled { background: #f3f4f6; color: #9ca3af; cursor: not-allowed; }
.cdr-send-btn { width: 32px; height: 32px; border-radius: 8px; background: #BC0C24; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; transition: background .14s, opacity .14s; }
.cdr-send-btn:hover:not(:disabled) { background: #9a0a1e; }
.cdr-send-btn:disabled { opacity: .38; cursor: default; }

/* ════════════════════════════════════════════
   Botão microfone
════════════════════════════════════════════ */
.cdr-mic-btn {
  width: 32px; height: 32px; border-radius: 8px;
  background: #f3f4f6; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #6b7280; flex-shrink: 0;
  transition: background .13s, color .13s;
}
.cdr-mic-btn:hover { background: #fee2e2; color: #BC0C24; }

/* ════════════════════════════════════════════
   Gravação de áudio
════════════════════════════════════════════ */
.cdr-recording, .cdr-rec-preview {
  display: flex; align-items: center; gap: 8px;
  padding: 9px 11px;
  background: #fff; border-top: 1px solid #f3f4f6;
  flex-shrink: 0;
}

/* Botões cancel / stop / send */
.cdr-rec-cancel {
  width: 30px; height: 30px; border-radius: 50%;
  background: #f3f4f6; border: none; cursor: pointer;
  color: #6b7280; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  transition: background .13s, color .13s;
}
.cdr-rec-cancel:hover { background: #e5e7eb; color: #374151; }

.cdr-rec-stop {
  width: 34px; height: 34px; border-radius: 50%;
  background: #BC0C24; border: none; cursor: pointer;
  color: #fff; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 6px rgba(188,12,36,.4);
  transition: background .13s, transform .1s;
}
.cdr-rec-stop:hover { background: #9a0a1e; transform: scale(1.07); }

.cdr-rec-send {
  width: 34px; height: 34px; border-radius: 50%;
  background: #BC0C24; border: none; cursor: pointer;
  color: #fff; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 2px 6px rgba(188,12,36,.4);
  transition: background .13s, transform .1s;
}
.cdr-rec-send:hover { background: #9a0a1e; transform: scale(1.07); }

/* Dot pulsante */
.cdr-rec-dot {
  width: 10px; height: 10px; border-radius: 50%;
  background: #BC0C24; flex-shrink: 0;
  animation: cdr-rec-pulse 1s ease-in-out infinite;
}
@keyframes cdr-rec-pulse {
  0%,100% { opacity: 1; transform: scale(1); }
  50%      { opacity: .4; transform: scale(.65); }
}

/* Waveform bars */
.cdr-rec-wave {
  flex: 1; display: flex; align-items: center;
  gap: 2px; height: 32px;
}
.cdr-rec-bar {
  width: 3px; background: #BC0C24; border-radius: 999px;
  min-height: 3px; transition: height .07s linear;
}

/* Timer */
.cdr-rec-time {
  font-size: 12px; font-weight: 700;
  color: #BC0C24; min-width: 34px; text-align: right; flex-shrink: 0;
}

/* Preview audio */
.cdr-rec-audio { flex: 1; height: 32px; min-width: 0; }

/* Bloco de gravação/preview de vídeo — layout vertical */
.cdr-vrec-wrap {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-top: 1px solid #f3f4f6;
  flex-shrink: 0;
}

/* Vídeo ocupa a largura total com proporção 16:9 */
.cdr-vrec-preview {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  background: #000;
  display: block;
}

/* Barra de controles abaixo do vídeo */
.cdr-vrec-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 11px;
}

/* Spinners */
.cdr-spinner { display: inline-block; width: 22px; height: 22px; border: 2.5px solid #e5e7eb; border-top-color: #BC0C24; border-radius: 50%; animation: cdr-spin .7s linear infinite; }
/* Botão de enviar tem fundo colorido — spinner claro. Nome diferente do
   .cdr-spinner-sm de cima (load more, sobre fundo claro) de propósito: as duas
   regras já colidiram uma vez (mesma especificidade, a última do arquivo vencia
   e deixava o spinner do "carregar mais antigas" branco-sobre-claro, invisível). */
.cdr-spinner-sm-white { display: inline-block; width: 13px; height: 13px; border: 2px solid rgba(255,255,255,.3); border-top-color: #fff; border-radius: 50%; animation: cdr-spin .7s linear infinite; }
@keyframes cdr-spin { to { transform: rotate(360deg); } }

/* ════════════════════════════════════════════
   Anexos nos balões
════════════════════════════════════════════ */
.cdr-att { margin-top: 6px; }

/* Imagem */
.cdr-att-img-wrap { display: block; }
.cdr-att-img {
  max-width: 220px; width: 100%; height: auto;
  border-radius: 10px; display: block;
  cursor: pointer;
}

/* ── Player de áudio ── */
.cdr-ap {
  display: flex; align-items: center; gap: 8px;
  padding: 6px 2px; min-width: 200px; max-width: 240px;
}
.cdr-ap-btn {
  width: 36px; height: 36px; border-radius: 50%;
  border: none; cursor: pointer; flex-shrink: 0;
  display: flex; align-items: center; justify-content: center;
  background: #BC0C24; color: #fff;
  transition: background .14s, transform .1s;
  box-shadow: 0 2px 6px rgba(188,12,36,.35);
}
.cdr-ap-btn:hover { transform: scale(1.08); }
.cdr-ap-btn:active { transform: scale(.93); }
.cdr-msg-row.out .cdr-ap-btn { background: rgba(255,255,255,.28); box-shadow: none; }
.cdr-msg-row.out .cdr-ap-btn:hover { background: rgba(255,255,255,.42); }

.cdr-ap-mid { flex: 1; display: flex; flex-direction: column; gap: 4px; }

.cdr-ap-bar {
  position: relative; height: 3px;
  background: rgba(0,0,0,.15); border-radius: 999px; cursor: pointer;
}
.cdr-msg-row.out .cdr-ap-bar { background: rgba(255,255,255,.28); }

.cdr-ap-fill {
  position: absolute; left: 0; top: 0; bottom: 0;
  background: #BC0C24; border-radius: 999px; pointer-events: none;
  transition: width .12s linear;
}
.cdr-msg-row.out .cdr-ap-fill { background: #fff; }

.cdr-ap-seek {
  position: absolute; left: 0; top: -9px;
  width: 100%; height: 21px; opacity: 0; cursor: pointer; margin: 0; padding: 0;
}

.cdr-ap-time {
  display: flex; justify-content: space-between;
  font-size: 9.5px; opacity: .55; color: inherit;
}

.cdr-ap-dl {
  color: inherit; opacity: .45; text-decoration: none;
  flex-shrink: 0; padding: 4px;
  transition: opacity .13s;
}
.cdr-ap-dl:hover { opacity: 1; }

/* Vídeo */
.cdr-att-video-wrap { display: block; }
.cdr-att-video {
  max-width: 240px;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 10px;
  display: block;
  background: #000;
}

/* PDF / arquivo */
.cdr-att-file {
  display: inline-flex; align-items: center; gap: 10px;
  background: rgba(0,0,0,.05); border-radius: 10px;
  padding: 9px 12px; text-decoration: none; color: inherit;
  transition: background .13s; max-width: 220px;
  border: 1px solid rgba(0,0,0,.07);
}
.cdr-att-file:hover { background: rgba(0,0,0,.10); }
.cdr-att-pdf svg:first-child { color: #e53e3e; }
.cdr-att-file-info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.cdr-att-file-ext { font-size: 12px; font-weight: 700; white-space: nowrap; }
.cdr-att-file-size { font-size: 10px; opacity: .55; }
.cdr-msg-row.out .cdr-att-file {
  background: rgba(255,255,255,.16); color: #fff;
  border-color: rgba(255,255,255,.18);
}
.cdr-msg-row.out .cdr-att-file:hover { background: rgba(255,255,255,.26); }
.cdr-msg-row.out .cdr-att-pdf svg:first-child { color: rgba(255,255,255,.8); }

/* ── Botão WhatsApp template ── */
.cdr-wa-tpl-btn {
  width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;
  background: #f3f4f6; border: none; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #6b7280; transition: background .13s, color .13s;
}
.cdr-wa-tpl-btn:hover { background: #fee2e2; color: #BC0C24; }

/* ── Modal template ── */
.cdr-tpl-overlay {
  position: fixed; inset: 0; z-index: 9000;
  background: rgba(0,0,0,.55); backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center; padding: 16px;
}
.cdr-tpl-dialog {
  background: #fff; border-radius: 12px;
  box-shadow: 0 24px 64px rgba(0,0,0,.22);
  width: min(780px, 100%); max-height: calc(100vh - 32px);
  display: flex; flex-direction: column; overflow: hidden;
}
.cdr-tpl-head {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px; border-bottom: 1px solid #e5e7eb; flex-shrink: 0;
}
.cdr-tpl-title { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 700; color: #111; }
.cdr-tpl-contact-name { font-size: 12px; font-weight: 500; color: #6b7280; }
.cdr-tpl-close { background: none; border: none; cursor: pointer; padding: 4px; color: #9ca3af; border-radius: 6px; display: flex; transition: background .15s; }
.cdr-tpl-close:hover { background: #f3f4f6; color: #374151; }
.cdr-tpl-body { display: grid; grid-template-columns: 260px 1fr; overflow: hidden; flex: 1; }
.cdr-tpl-list-col { padding: 14px; border-right: 1px solid #e5e7eb; overflow-y: auto; display: flex; flex-direction: column; gap: 4px; }
.cdr-tpl-config-col { padding: 14px 18px; overflow-y: auto; }
.cdr-tpl-col-lbl { font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .06em; margin-bottom: 8px; }
.cdr-tpl-loading { display: flex; align-items: center; gap: 6px; color: #9ca3af; font-size: 12px; padding: 12px 0; }
.cdr-tpl-items { display: flex; flex-direction: column; gap: 4px; }
.cdr-tpl-item { width: 100%; text-align: left; padding: 8px 10px; border: 1.5px solid #e5e7eb; border-radius: 7px; background: #fff; cursor: pointer; transition: border-color .15s, background .15s; }
.cdr-tpl-item:hover { background: #f9fafb; border-color: #d1d5db; }
.cdr-tpl-item.active { border-color: #25D366; background: rgba(37,211,102,.05); }
.cdr-tpl-item-name { font-size: 12px; font-weight: 600; color: #111; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 4px; }
.cdr-tpl-item-meta { display: flex; align-items: center; gap: 5px; }
.cdr-tpl-cat { font-size: 9.5px; font-weight: 700; text-transform: uppercase; padding: 1px 5px; border-radius: 4px; }
.cdr-tpl-cat.marketing { background: rgba(251,191,36,.15); color: #b45309; }
.cdr-tpl-cat.utility   { background: rgba(59,130,246,.12); color: #1d4ed8; }
.cdr-tpl-lang { font-size: 10px; color: #9ca3af; }
.cdr-tpl-pick-hint { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; height: 100%; color: #9ca3af; font-size: 12px; text-align: center; padding: 40px 20px; }
.cdr-tpl-preview-wrap { background: #e5ddd5; border-radius: 8px; padding: 10px; }
.cdr-tpl-bubble { background: #fff; border-radius: 0 10px 10px 10px; padding: 10px; max-width: 90%; box-shadow: 0 1px 3px rgba(0,0,0,.1); font-family: -apple-system,sans-serif; }
.cdr-tpl-bubble-img { width: 100%; border-radius: 7px; height: 110px; overflow: hidden; margin-bottom: 8px; background: #c8d8e0; display: flex; align-items: center; justify-content: center; }
.cdr-tpl-bubble-img img { width:100%; height:100%; object-fit:cover; }
.cdr-tpl-img-ph { display:flex; align-items:center; justify-content:center; color:#8a9ca5; }
.cdr-tpl-bubble-hdr { font-size: 13px; font-weight: 700; color: #1a1a1a; margin-bottom: 4px; }
.cdr-tpl-bubble-body { font-size: 12.5px; color: #1a1a1a; line-height: 1.5; white-space: pre-wrap; word-break: break-word; }
.cdr-tpl-bubble-ftr { font-size: 10.5px; color: #8a8a8a; margin-top: 6px; padding-top: 6px; border-top: 1px solid #f0f0f0; }
.cdr-tpl-bubble-time { text-align: right; font-size: 9.5px; color: #999; margin-top: 3px; }
.cdr-tpl-btns { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; }
.cdr-tpl-btn { background: #fff; border-radius: 8px; padding: 7px 10px; font-size: 12px; font-weight: 500; color: #00a5f4; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,.08); }
.cdr-tpl-field-lbl { font-size: 10px; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .05em; margin-bottom: 3px; }
.cdr-tpl-input { width: 100%; padding: 7px 10px; border: 1.5px solid #e2e8f0; border-radius: 6px; font-size: 12.5px; font-family: inherit; outline: none; box-sizing: border-box; transition: border-color .15s; }
.cdr-tpl-input:focus { border-color: #25D366; }
.cdr-tpl-hint { font-size: 10px; color: #9ca3af; margin-top: 2px; }
.cdr-tpl-mapped-badge { display: flex; align-items: center; gap: 6px; background: rgba(255,102,0,.08); color: #cc5200; border: 1px solid rgba(255,102,0,.2); border-radius: 6px; padding: 7px 10px; font-size: 12px; font-weight: 600; }
.cdr-tpl-send-btn { width: 100%; padding: 10px; background: #25D366; color: #fff; border: none; border-radius: 8px; font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .15s; }
.cdr-tpl-send-btn:hover:not(:disabled) { background: #1db954; }
.cdr-tpl-send-btn:disabled { opacity: .5; cursor: not-allowed; }
.cdr-tpl-blocked-hint { font-size: 11.5px; color: #b45309; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 6px 8px; margin-bottom: 8px; line-height: 1.4; }
.cdr-tpl-spin { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(0,0,0,.15); border-top-color: #374151; border-radius: 50%; animation: cdr-spin .7s linear infinite; }
@keyframes cdr-spin { to { transform: rotate(360deg); } }

/* ── Snooze badge on strip avatar ── */
.cdr-av-snooze-badge {
  position: absolute; bottom: -2px; right: -2px;
  width: 14px; height: 14px; border-radius: 50%;
  background: #f59e0b; border: 1.5px solid #021627;
  display: flex; align-items: center; justify-content: center;
  color: #fff; z-index: 2;
}

/* ── Status label pill in chat header subtitle ── */
.cdr-status-lbl {
  display: inline-flex; align-items: center;
  padding: 2px 7px; border-radius: 4px;
  font-size: 9.5px; font-weight: 800; letter-spacing: .5px;
  text-transform: uppercase; flex-shrink: 0;
}
.cdr-status-lbl.open     { background: rgba(34,197,94,.22); color: #86efac; border: 1px solid rgba(34,197,94,.3); }
.cdr-status-lbl.pending  { background: rgba(249,115,22,.25); color: #fdba74; border: 1px solid rgba(249,115,22,.35); }
.cdr-status-lbl.snoozed  { background: rgba(245,158,11,.25); color: #fcd34d; border: 1px solid rgba(245,158,11,.35); }
.cdr-status-lbl.resolved { background: rgba(255,255,255,.1);  color: rgba(255,255,255,.6); border: 1px solid rgba(255,255,255,.18); }

/* Agent tag in header */
.cdr-agent-tag {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 7px; border-radius: 6px; flex-shrink: 0;
  font-size: 10px; font-weight: 600;
  background: rgba(255,255,255,.1); color: rgba(255,255,255,.8);
  border: 1px solid rgba(255,255,255,.18);
}
.cdr-agent-tag svg { opacity: .75; flex-shrink: 0; }

/* SLA badge in header */
.cdr-sla-badge {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 7px; border-radius: 6px; flex-shrink: 0;
  font-size: 10px;
}
.cdr-sla-badge strong { font-size: 11px; font-weight: 800; }
.cdr-sla-badge.breached { background: rgba(239,68,68,.25); color: #fca5a5; border: 1px solid rgba(239,68,68,.35); }
.cdr-sla-badge.atRisk   { background: rgba(249,115,22,.25); color: #fdba74; border: 1px solid rgba(249,115,22,.35); }

/* ── Status change dropdown ── */
.cdr-status-wrap { position: relative; display: flex; align-items: center; }

.cdr-s-dot {
  width: 8px; height: 8px; border-radius: 50%; display: inline-block; flex-shrink: 0;
}
.cdr-s-dot.open     { background: #22c55e; }
.cdr-s-dot.resolved { background: #6b7280; }
.cdr-s-dot.pending  { background: #f97316; }
.cdr-s-dot.snoozed  { background: #f59e0b; }

.cdr-status-menu {
  position: absolute; top: calc(100% + 6px); right: 0; z-index: 200;
  min-width: 148px; background: #fff;
  border: 1px solid rgba(0,0,0,.1); border-radius: 10px;
  box-shadow: 0 8px 28px rgba(0,0,0,.18); overflow: hidden;
}
.cdr-sopt {
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 9px 12px; background: none; border: none;
  font-size: 12.5px; font-weight: 500; color: #1f2937;
  cursor: pointer; font-family: inherit; text-align: left;
  transition: background .12s;
}
.cdr-sopt:hover { background: #f9fafb; }

/* Snooze form */
.cdr-snz { padding: 12px; display: flex; flex-direction: column; gap: 8px; min-width: 200px; }
.cdr-snz-title { font-size: 11.5px; font-weight: 700; color: #1f2937; }
.cdr-snz-row { display: flex; align-items: center; gap: 6px; }
.cdr-snz-inp {
  width: 52px; padding: 5px 7px; border: 1.5px solid #e5e7eb;
  border-radius: 6px; font-size: 13px; font-family: inherit;
  background: #f9fafb; outline: none; text-align: center;
}
.cdr-snz-inp:focus { border-color: #BC0C24; background: #fff; }
.cdr-snz-lbl { font-size: 12px; color: #6b7280; font-weight: 500; }
.cdr-snz-hint { font-size: 10px; color: #9ca3af; }
.cdr-snz-btns { display: flex; gap: 6px; }
.cdr-snz-cancel {
  flex: 1; padding: 7px; border: 1px solid #e5e7eb; border-radius: 6px;
  background: #fff; font-size: 12px; font-family: inherit; color: #6b7280; cursor: pointer;
}
.cdr-snz-cancel:hover { background: #f9fafb; }
.cdr-snz-confirm {
  flex: 1; padding: 7px; border: none; border-radius: 6px;
  background: #BC0C24; color: #fff; font-size: 12px;
  font-family: inherit; font-weight: 600; cursor: pointer;
}
.cdr-snz-confirm:hover:not(:disabled) { background: #9b0a1e; }
.cdr-snz-confirm:disabled { opacity: .5; cursor: not-allowed; }

/* ── Snooze banner ── */
.cdr-snooze-banner {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; background: rgba(245,158,11,.1);
  border-bottom: 1px solid rgba(245,158,11,.25);
  font-size: 11.5px; font-weight: 600; color: #92400e;
  flex-shrink: 0;
}
.cdr-snooze-banner svg { flex-shrink: 0; color: #f59e0b; }

.cdr-window-banner {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; background: #fef2f2;
  border-bottom: 1px solid #fecaca;
  font-size: 11.5px; font-weight: 600; color: #b91c1c;
  flex-shrink: 0;
}
.cdr-window-banner svg { flex-shrink: 0; color: #dc2626; }

/* ── Message search ── */
.cdr-msg-search-bar {
  display: flex; align-items: center; gap: 4px;
  padding: 5px 8px; background: #fafafa;
  border-bottom: 1px solid var(--ink-10); flex-shrink: 0;
}
.cdr-msg-search-input {
  flex: 1; border: 1px solid var(--ink-15); border-radius: 6px;
  padding: 4px 8px; font-size: 11px; outline: none;
  background: var(--white); color: var(--ink-90);
}
.cdr-msg-search-input:focus { border-color: #BC0C24; }
.cdr-msg-search-count {
  font-size: 10px; color: var(--ink-50); white-space: nowrap; flex-shrink: 0;
}
.cdr-msg-search-nav,
.cdr-msg-search-close {
  background: none; border: none; cursor: pointer; padding: 3px 4px;
  color: var(--ink-50); border-radius: 4px; display: flex; align-items: center; flex-shrink: 0;
}
.cdr-msg-search-nav:hover,
.cdr-msg-search-close:hover { background: var(--ink-5); color: var(--ink-90); }
.msg-match-current { outline: 2px solid #f59e0b; outline-offset: -1px; border-radius: 8px; }
mark.msg-hl { background: #fef08a; color: #78350f; border-radius: 2px; padding: 0 1px; }

/* ── Macro panel button state ── */
.cdr-icon-btn--active { background: rgba(188,12,36,.12) !important; color: #BC0C24 !important; }

/* ── Macro panel ── */
.cdr-macro-panel {
  flex: 1; display: flex; flex-direction: column; overflow: hidden;
  background: var(--white); border-top: 1px solid var(--ink-10);
}
.cdr-macro-head {
  display: flex; align-items: center; gap: 8px;
  padding: 8px 10px; border-bottom: 1px solid var(--ink-10); flex-shrink: 0;
}
.cdr-macro-search-wrap {
  flex: 1; display: flex; align-items: center; gap: 6px;
  background: var(--ink-05); border: 1px solid var(--ink-10); border-radius: 6px;
  padding: 4px 8px;
}
.cdr-macro-search-wrap svg { color: var(--ink-40); flex-shrink: 0; }
.cdr-macro-search {
  flex: 1; border: none; background: transparent; outline: none;
  font-size: 11.5px; color: var(--ink-80); font-family: inherit;
}
.cdr-macro-count { font-size: 10.5px; color: var(--ink-40); white-space: nowrap; flex-shrink: 0; }
.cdr-macro-back {
  display: flex; align-items: center; gap: 4px;
  background: none; border: none; cursor: pointer; padding: 3px 6px;
  color: var(--ink-60); font-size: 11px; font-family: inherit; border-radius: 4px;
  flex-shrink: 0;
}
.cdr-macro-back:hover { background: var(--ink-05); }
.cdr-macro-run-title { flex: 1; font-size: 11.5px; font-weight: 600; color: var(--ink-80); truncate: true; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.cdr-macro-loading { flex: 1; display: flex; align-items: center; justify-content: center; }
.cdr-macro-list { flex: 1; overflow-y: auto; padding: 4px 0; }
.cdr-macro-item {
  display: flex; align-items: center; gap: 8px; width: 100%;
  padding: 8px 12px; background: none; border: none; cursor: pointer;
  text-align: left; color: var(--ink-80); font-family: inherit;
  border-bottom: 1px solid var(--ink-05); transition: background .15s;
}
.cdr-macro-item:hover { background: var(--ink-05); }
.cdr-macro-item svg { flex-shrink: 0; color: #BC0C24; }
.cdr-macro-item-body { flex: 1; overflow: hidden; }
.cdr-macro-item-name { display: block; font-size: 12px; font-weight: 600; color: var(--ink-80); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.cdr-macro-item-sub { display: block; font-size: 10.5px; color: var(--ink-40); margin-top: 1px; }
.cdr-macro-empty { padding: 20px; text-align: center; font-size: 12px; color: var(--ink-40); }
.cdr-macro-steps { flex: 1; overflow-y: auto; padding: 6px 0; }
.cdr-macro-step {
  display: flex; align-items: flex-start; gap: 7px;
  padding: 6px 12px; font-size: 11.5px; border-bottom: 1px solid var(--ink-05);
}
.cdr-macro-step--ok svg { color: #16a34a; flex-shrink: 0; margin-top: 1px; }
.cdr-macro-step--err svg { color: #dc2626; flex-shrink: 0; margin-top: 1px; }
.cdr-macro-step-label { flex: 1; color: var(--ink-80); }
.cdr-macro-step-msg { font-size: 10px; color: #dc2626; margin-top: 2px; display: block; }
.cdr-macro-summary {
  display: flex; align-items: center; gap: 7px;
  padding: 8px 12px; font-size: 11.5px; font-weight: 600; flex-shrink: 0;
}
.cdr-macro-summary--ok { background: rgba(22,163,74,.1); color: #16a34a; }
.cdr-macro-summary--warn { background: rgba(245,158,11,.1); color: #92400e; }

/* ── Macro inline form ── */
.cdr-macro-form {
  flex: 1; overflow-y: auto; padding: 12px;
  display: flex; flex-direction: column; gap: 12px;
  background: #f8f9fa;
}
.cdr-mform-card {
  background: #fff; border: 1px solid #e5e7eb; border-radius: 10px;
  padding: 14px 14px 10px; display: flex; flex-direction: column; gap: 10px;
}
.cdr-mform-card-header { display: flex; align-items: flex-start; gap: 10px; }
.cdr-mform-num {
  width: 22px; height: 22px; border-radius: 50%;
  background: #1f2937; color: #fff;
  font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.cdr-mform-card-info { display: flex; flex-direction: column; gap: 2px; overflow: hidden; }
.cdr-mform-card-title { font-size: 12.5px; font-weight: 700; color: #111827; }
.cdr-mform-card-url {
  font-size: 10.5px; color: #6b7280;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.cdr-mform-hint { font-size: 11.5px; font-weight: 600; color: #374151; margin: 0; }
.cdr-mform-field { display: flex; flex-direction: column; gap: 4px; }
.cdr-mform-label { font-size: 12px; font-weight: 600; color: #374151; }
.cdr-mform-input {
  border: 1px solid #d1d5db; border-radius: 7px;
  padding: 8px 11px; font-size: 13px; font-family: inherit;
  color: #111827; background: #fff; outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.cdr-mform-input:focus { border-color: #BC0C24; box-shadow: 0 0 0 3px rgba(188,12,36,.1); }
.cdr-mform-btns { margin-top: 2px; }
.cdr-mform-submit {
  display: flex; align-items: center; gap: 7px; width: 100%;
  justify-content: center; padding: 10px 14px;
  background: #BC0C24; color: #fff; border: none; border-radius: 8px;
  font-size: 13px; font-weight: 600; font-family: inherit; cursor: pointer;
  transition: background .15s;
}
.cdr-mform-submit:hover { background: #a00a1e; }
.cdr-mform-submit svg { stroke: #fff; flex-shrink: 0; }
</style>
